// src/components/DirectPOSView.js

import React, { useEffect, useRef, useState } from "react";
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  Utensils,
  Package,
  X,
} from "lucide-react";

import {
  getAvailableMenuItems,
  getCategories,
  getTables,
  createDirectOrder,
  getBusinessProfile,
  getBusinessLogoBlob,
} from "../api";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8080";

// =========================================================
// DEFAULT "NO IMAGE" ILLUSTRATION — plate & spoon, SwiftOrder style
// Used both as a React fallback element and, as a raw string,
// inside the onError innerHTML swap (img tags can't onError-recover
// to JSX, only to markup).
// =========================================================

const PLATE_SPOON_SVG_MARKUP = `
  <div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f2ede1;">
    <svg width="54" height="54" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="32" cy="32" r="27" fill="#fbf9f4" stroke="#ddd4c2" stroke-width="2"/>
      <circle cx="32" cy="32" r="17" fill="none" stroke="#ddd4c2" stroke-width="2"/>
      <path d="M17 16 L17 30 C17 32.2 18.8 34 21 34 C23.2 34 25 32.2 25 30 L25 16" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
      <line x1="21" y1="16" x2="21" y2="48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
      <path d="M45 16 C41 16 39 20 39 25 C39 29 41 31 45 31 L45 48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round" fill="none"/>
    </svg>
  </div>
`;

function PlateSpoonPlaceholder() {
  return (
    <div className="so-no-image">
      <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="32" cy="32" r="27" fill="#fbf9f4" stroke="#ddd4c2" strokeWidth="2" />
        <circle cx="32" cy="32" r="17" fill="none" stroke="#ddd4c2" strokeWidth="2" />
        <path d="M17 16 L17 30 C17 32.2 18.8 34 21 34 C23.2 34 25 32.2 25 30 L25 16" stroke="#a49c8d" strokeWidth="2" strokeLinecap="round" />
        <line x1="21" y1="16" x2="21" y2="48" stroke="#a49c8d" strokeWidth="2" strokeLinecap="round" />
        <path d="M45 16 C41 16 39 20 39 25 C39 29 41 31 45 31 L45 48" stroke="#a49c8d" strokeWidth="2" strokeLinecap="round" fill="none" />
      </svg>
    </div>
  );
}

export default function DirectPOSView() {
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const [cart, setCart] = useState([]);

  const [orderType, setOrderType] = useState("TAKEAWAY");
  const [orderChannel, setOrderChannel] = useState("POS");
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);

  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountAmountInput, setDiscountAmountInput] = useState(0);
  const [cashAmount, setCashAmount] = useState(0);
  const [onlineAmount, setOnlineAmount] = useState(0);

  const [specialInstructions, setSpecialInstructions] = useState("");

  const [businessProfile, setBusinessProfile] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingMenu, setLoadingMenu] = useState(true);
  const [loadingTables, setLoadingTables] = useState(true);

  const [showCartDrawer, setShowCartDrawer] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const receiptRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    loadMenu();
    loadCategories();
    loadTables();
    loadBusinessProfile();
  }, []);

  const loadMenu = async () => {
    try {
      setLoadingMenu(true);

      const data = await getAvailableMenuItems();

      setMenuItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load menu:", error);
      alert("Unable to load menu items.");
    } finally {
      setLoadingMenu(false);
    }
  };

  const loadCategories = async () => {
    try {
      const data = await getCategories();

      setCategories(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const loadTables = async () => {
    try {
      setLoadingTables(true);

      const data = await getTables();

      setTables(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to load tables:", error);
      setTables([]);
    } finally {
      setLoadingTables(false);
    }
  };

  const loadBusinessProfile = async () => {
    try {
      const data = await getBusinessProfile();

      if (!data) return;

      setBusinessProfile(data);

      if (data.hasLogo) {
        try {
          const response = await getBusinessLogoBlob();

          const url = URL.createObjectURL(response.data);

          setLogoUrl(url);
        } catch (error) {
          console.error("Failed to load business logo:", error);
        }
      }
    } catch (error) {
      console.error("Failed to load business profile:", error);
    }
  };

  useEffect(() => {
    return () => {
      if (logoUrl) {
        URL.revokeObjectURL(logoUrl);
      }
    };
  }, [logoUrl]);

  const toNumber = (value) => {
    const number = Number(value);

    return Number.isFinite(number) ? number : 0;
  };

  const roundMoney = (value) => {
    return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
  };

  const gstEnabled = Boolean(businessProfile?.gstEnabled);

  const businessDefaultTaxRate = gstEnabled
    ? toNumber(businessProfile?.defaultTaxRate)
    : 0;

  const getItemTaxRate = (menuItem) => {
    const itemRate = toNumber(menuItem?.taxRate);

    if (itemRate > 0) {
      return itemRate;
    }

    return businessDefaultTaxRate;
  };

  /*
   * DIRECT POS GST IS ALWAYS EXTRA.
   *
   * ₹500 + 5% GST = ₹525
   * ₹500 + 3% GST = ₹515
   */
  const calculateLine = (menuItem, quantity) => {
    const price = roundMoney(menuItem?.price);

    const qty = Math.max(
      0,
      Math.floor(toNumber(quantity))
    );

    const subtotal = roundMoney(price * qty);

    if (!gstEnabled) {
      return {
        subtotal,
        taxRate: 0,
        taxAmount: 0,
        total: subtotal,
      };
    }

    const taxRate = roundMoney(
      getItemTaxRate(menuItem)
    );

    if (taxRate <= 0) {
      return {
        subtotal,
        taxRate: 0,
        taxAmount: 0,
        total: subtotal,
      };
    }

    const taxAmount = roundMoney(
      subtotal * taxRate / 100
    );

    const total = roundMoney(
      subtotal + taxAmount
    );

    return {
      subtotal,
      taxRate,
      taxAmount,
      total,
    };
  };

  const calculateCartTotals = () => {
    let subtotal = 0;
    let taxAmount = 0;
    let grossTotal = 0;

    cart.forEach((item) => {
      const line = calculateLine(
        item.menuItem,
        item.quantity
      );

      subtotal = roundMoney(subtotal + line.subtotal);
      taxAmount = roundMoney(taxAmount + line.taxAmount);
      grossTotal = roundMoney(grossTotal + line.total);
    });

    let percent = Math.max(0, Math.min(100, toNumber(discountPercent)));
    let amount = Math.max(0, toNumber(discountAmountInput));

    // Match the existing POS discount convention:
    // percentage discount is calculated from subtotal + GST.
    if (percent > 0) {
      amount = roundMoney(grossTotal * percent / 100);
    }

    amount = Math.min(amount, grossTotal);

    const totalAmount = roundMoney(
      Math.max(0, grossTotal - amount)
    );

    return {
      subtotal,
      taxAmount,
      grossTotal,
      discountPercent: roundMoney(percent),
      discountAmount: roundMoney(amount),
      totalAmount,
    };
  };

  const filteredItems =
    selectedCategory === "all"
      ? menuItems
      : menuItems.filter(
          (item) =>
            item.category &&
            String(item.category.id) ===
              String(selectedCategory)
        );

  const addToCart = (menuItem) => {
    setCart((previousCart) => {
      const existing = previousCart.find(
        (item) =>
          String(item.menuItem.id) ===
          String(menuItem.id)
      );

      if (existing) {
        return previousCart.map((item) =>
          String(item.menuItem.id) ===
          String(menuItem.id)
            ? {
                ...item,
                quantity: item.quantity + 1,
              }
            : item
        );
      }

      return [
        ...previousCart,
        {
          menuItem,
          quantity: 1,
        },
      ];
    });
  };

  const updateQuantity = (menuItemId, change) => {
    setCart((previousCart) =>
      previousCart
        .map((item) =>
          String(item.menuItem.id) ===
          String(menuItemId)
            ? {
                ...item,
                quantity:
                  item.quantity + change,
              }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (menuItemId) => {
    setCart((previousCart) =>
      previousCart.filter(
        (item) =>
          String(item.menuItem.id) !==
          String(menuItemId)
      )
    );
  };

  const getTotalItems = () => {
    return cart.reduce(
      (total, item) =>
        total + item.quantity,
      0
    );
  };

  const handleOrderTypeChange = (type) => {
    if (type === "DINE_IN" && orderChannel !== "POS") {
      alert("Swiggy and Zomato orders must be Takeaway.");
      return;
    }

    setOrderType(type);

    if (type === "TAKEAWAY") {
      setSelectedTable(null);
    }
  };

  // Swiggy / Zomato are sales channels, separate from DINE_IN / TAKEAWAY.
  // Marketplace orders are treated as takeaway and do not occupy a table.
  const handleOrderChannelChange = (channel) => {
    setOrderChannel(channel);

    if (channel !== "POS") {
      setOrderType("TAKEAWAY");
      setSelectedTable(null);
    }
  };

  const formatOrderChannel = (channel) => {
    switch (channel) {
      case "SWIGGY":
        return "Swiggy";
      case "ZOMATO":
        return "Zomato";
      default:
        return "Direct POS";
    }
  };

  const handleTableSelect = (table) => {
    if (!table) return;

    if (table.status === "OCCUPIED") {
      alert(
        `${table.tableName} is currently occupied.`
      );
      return;
    }

    if (table.status === "OUT_OF_SERVICE") {
      alert(
        `${table.tableName} is out of service.`
      );
      return;
    }

    setSelectedTable(table);
  };

  const generateOrderNumber = () => {
    return `POS-${Date.now()}`;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(toNumber(value));
  };

  const formatDateTime = (value) => {
    if (!value) return "";

    return new Date(value).toLocaleString("en-IN");
  };

  const formatOrderType = (type) => {
    return type === "DINE_IN"
      ? "🍽️ DINE IN"
      : "🥡 TAKEAWAY";
  };

  const handleGenerateBill = () => {
    if (cart.length === 0) {
      alert("Please add at least one item.");
      return;
    }

    if (orderType === "DINE_IN" && !selectedTable) {
      alert("Please select a table for Dine In.");
      return;
    }

    if (orderChannel !== "POS" && orderType !== "TAKEAWAY") {
      alert("Swiggy and Zomato orders must be Takeaway.");
      return;
    }

    const totals = calculateCartTotals();
    const cash = roundMoney(cashAmount);
    const online = roundMoney(onlineAmount);
    const paid = roundMoney(cash + online);

    if (cash < 0 || online < 0) {
      alert("Cash and online amounts cannot be negative.");
      return;
    }

    if (paid > totals.totalAmount) {
      alert("Cash + online amount cannot exceed the final bill amount.");
      return;
    }

    const orderNumber = generateOrderNumber();

    const invoiceItems = cart.map((item) => {
      const line = calculateLine(item.menuItem, item.quantity);

      return {
        id: item.menuItem.id,
        name: item.menuItem.name,
        price: roundMoney(item.menuItem.price),
        quantity: item.quantity,
        taxRate: line.taxRate,
        taxAmount: line.taxAmount,
        subtotal: line.subtotal,
        lineTotal: line.total,
      };
    });

    setInvoiceData({
      orderNumber,
      orderType,
      orderChannel,
      table: selectedTable,
      specialInstructions,
      createdAt: new Date().toISOString(),

      gstEnabled,
      taxInclusive: false,
      defaultTaxRate: businessDefaultTaxRate,

      subtotal: totals.subtotal,
      taxAmount: totals.taxAmount,
      grossTotal: totals.grossTotal,
      discountPercent: totals.discountPercent,
      discountAmount: totals.discountAmount,
      totalAmount: totals.totalAmount,

      cashAmount: cash,
      onlineAmount: online,
      paidAmount: paid,
      balanceDue: roundMoney(totals.totalAmount - paid),
      paymentStatus:
        paid >= totals.totalAmount
          ? "PAID"
          : paid > 0
          ? "PARTIALLY_PAID"
          : "UNPAID",

      items: invoiceItems,
    });

    setShowInvoice(true);
  };

  const handleInvoiceDone = async () => {
    if (!invoiceData) return;

    setIsProcessing(true);

    try {
      const payload = {
        orderNumber:
          invoiceData.orderNumber,

        orderType:
          invoiceData.orderType,

        orderChannel:
          invoiceData.orderChannel || "POS",

        tableId:
          invoiceData.orderType === "DINE_IN"
            ? invoiceData.table?.id
            : null,

        specialInstructions:
          invoiceData.specialInstructions || null,

        discountPercent:
          toNumber(invoiceData.discountPercent),

        discountAmount:
          toNumber(invoiceData.discountAmount),

        cashAmount:
          toNumber(invoiceData.cashAmount),

        onlineAmount:
          toNumber(invoiceData.onlineAmount),

        items: invoiceData.items.map(
          (item) => ({
            menuItemId: item.id,
            quantity: item.quantity,

            /*
             * IMPORTANT:
             * Send ORIGINAL menu price.
             *
             * ₹500 is sent.
             * Backend calculates:
             * GST ₹25
             * Total ₹525
             */
            price: item.price,

            specialInstructions: null,

            taxRate: item.taxRate,
          })
        ),
      };

      const savedOrder =
        await createDirectOrder(payload);

      console.log(
        "DIRECT ORDER SAVED:",
        savedOrder
      );

      setCart([]);
      setOrderType("TAKEAWAY");
      setOrderChannel("POS");
      setSelectedTable(null);
      setDiscountPercent(0);
      setDiscountAmountInput(0);
      setCashAmount(0);
      setOnlineAmount(0);
      setSpecialInstructions("");
      setInvoiceData(null);
      setShowInvoice(false);
      setShowCartDrawer(false);

      await loadTables();

      alert("Order saved successfully.");
    } catch (error) {
      console.error(
        "Direct POS order failed:",
        error
      );

      console.error(
        "Server response:",
        error?.response?.data
      );

      const serverMessage =
        error?.response?.data;

      alert(
        typeof serverMessage === "string"
          ? serverMessage
          : serverMessage?.message ||
            "Unable to save order."
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePrint = () => {
    if (!receiptRef.current) return;

    const content =
      receiptRef.current.innerHTML;

    const printWindow = window.open(
      "",
      "_blank",
      "width=420,height=750"
    );

    if (!printWindow) {
      alert(
        "Popup blocked. Please allow popups to print."
      );
      return;
    }

    printWindow.document.open();

    printWindow.document.write(`
      <html>
        <head>
          <title>Invoice</title>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1"
          />

          <style>
            * {
              box-sizing: border-box;
            }

            body {
              font-family: Arial, Helvetica, sans-serif;
              margin: 0;
              padding: 20px;
              background: white;
              color: #111827;
            }

            .row {
              display: flex;
              justify-content: space-between;
              gap: 10px;
            }

            img {
              max-width: 120px;
              max-height: 70px;
              object-fit: contain;
            }
          </style>
        </head>

        <body>
          ${content}
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  const handleDownloadPDF = async () => {
    if (
      !receiptRef.current ||
      !invoiceData
    ) {
      return;
    }

    const element = receiptRef.current;

    const originalWidth =
      element.style.width;

    const originalMaxHeight =
      element.style.maxHeight;

    const originalPaddingBottom =
      element.style.paddingBottom;

    try {
      const targetWidth =
        window.innerWidth > 900
          ? 700
          : Math.floor(
              window.innerWidth * 0.92
            );

      element.style.width =
        `${targetWidth}px`;

      element.style.maxHeight = "none";
      element.style.paddingBottom = "60px";

      const canvas =
        await html2canvas(
          element,
          {
            scale:
              Math.min(
                2,
                window.devicePixelRatio ||
                  1.5
              ) * 1.2,

            useCORS: true,

            scrollX: 0,
            scrollY: -window.scrollY,

            windowWidth:
              element.scrollWidth,

            windowHeight:
              element.scrollHeight,
          }
        );

      const imageData =
        canvas.toDataURL("image/png");

      const pdf =
        new jsPDF(
          "p",
          "pt",
          "a4"
        );

      const pageWidth =
        pdf.internal.pageSize.getWidth();

      const pageHeight =
        pdf.internal.pageSize.getHeight();

      const margin = 20;

      const imageWidth =
        pageWidth - margin * 2;

      const imageHeight =
        (canvas.height *
          imageWidth) /
        canvas.width;

      let heightLeft =
        imageHeight;

      let position = margin;

      pdf.addImage(
        imageData,
        "PNG",
        margin,
        position,
        imageWidth,
        imageHeight
      );

      heightLeft -=
        pageHeight - margin * 2;

      while (heightLeft > 0) {
        pdf.addPage();

        position =
          margin -
          (
            imageHeight -
            heightLeft
          );

        pdf.addImage(
          imageData,
          "PNG",
          margin,
          position,
          imageWidth,
          imageHeight
        );

        heightLeft -=
          pageHeight - margin * 2;
      }

      pdf.save(
        `invoice_${invoiceData.orderNumber}.pdf`
      );
    } catch (error) {
      console.error(
        "PDF generation failed:",
        error
      );

      alert(
        "Failed to generate PDF."
      );
    } finally {
      element.style.width =
        originalWidth || "";

      element.style.maxHeight =
        originalMaxHeight || "";

      element.style.paddingBottom =
        originalPaddingBottom || "";
    }
  };

  /*
   * This is a plain render function, called as renderCart() inside JSX.
   * Do not turn it back into a nested component: a component defined
   * inside another component gets a new identity on every render, so
   * React remounts it on each keystroke. That made the Order Notes
   * textarea lose focus (and the keyboard close) after every letter.
   */
  const renderCart = ({ mobile = false } = {}) => {
    const totals =
      calculateCartTotals();

    return (
      <>
        {cart.length === 0 ? (
          <div className="text-center py-10 so-text-stone-light">
            <ShoppingCart
              size={48}
              className="mx-auto mb-3 opacity-40"
            />

            <p>Cart is empty</p>

            <p className="text-xs mt-1">
              Add menu items to continue.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {cart.map((item) => {
              const line =
                calculateLine(
                  item.menuItem,
                  item.quantity
                );

              return (
                <div
                  key={item.menuItem.id}
                  className="so-cart-line rounded-lg p-3"
                >
                  <div className="flex justify-between items-start">
                    <div className="pr-2">
                      <h4 className="font-semibold so-text-ink">
                        {item.menuItem.name}
                      </h4>

                      <p className="text-xs so-text-stone mt-1">
                        {formatCurrency(
                          item.menuItem.price
                        )}{" "}
                        each
                      </p>

                      {gstEnabled &&
                        line.taxRate > 0 && (
                          <p className="text-xs so-text-stone mt-1">
                            GST{" "}
                            {line.taxRate}%
                            {" + "}
                            {formatCurrency(
                              line.taxAmount
                            )}
                          </p>
                        )}
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        removeFromCart(
                          item.menuItem.id
                        )
                      }
                      className="so-remove-btn"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="flex justify-between items-center mt-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(
                            item.menuItem.id,
                            -1
                          )
                        }
                        className="so-qty-btn"
                      >
                        <Minus size={15} />
                      </button>

                      <span className="w-8 text-center font-semibold so-text-ink">
                        {item.quantity}
                      </span>

                      <button
                        type="button"
                        onClick={() =>
                          updateQuantity(
                            item.menuItem.id,
                            1
                          )
                        }
                        className="so-qty-btn"
                      >
                        <Plus size={15} />
                      </button>
                    </div>

                    <div className="text-right">
                      <div className="font-bold so-text-ink">
                        {formatCurrency(
                          line.total
                        )}
                      </div>

                      {gstEnabled &&
                        line.taxAmount > 0 && (
                          <div className="text-xs so-text-stone">
                            GST{" "}
                            {formatCurrency(
                              line.taxAmount
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4">
          <label className="block text-sm font-semibold so-text-ink mb-1">
            Order Notes
          </label>

          <textarea
            value={specialInstructions}
            onChange={(e) =>
              setSpecialInstructions(
                e.target.value
              )
            }
            rows={3}
            maxLength={500}
            placeholder="e.g. Less spicy, extra sauce, no onion..."
            className="so-textarea w-full rounded-lg p-3 text-sm"
          />

          <div className="text-right text-xs so-text-stone-light mt-1">
            {specialInstructions.length}/500
          </div>
        </div>

        <div className="so-dashed-divider mt-4 pt-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="so-text-stone">
              {getTotalItems()} item
              {getTotalItems() !== 1
                ? "s"
                : ""}
            </span>
          </div>

          <div className="rounded-lg p-3 mb-3" style={{ background: "#f7f2e8", border: "1px solid var(--line)" }}>
            <div className="font-semibold text-sm so-text-ink mb-2">
              Discount
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs so-text-stone mb-1">
                  Discount (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={discountPercent}
                  onChange={(e) => {
                    setDiscountPercent(
                      Math.max(0, Math.min(100, toNumber(e.target.value)))
                    );
                  }}
                  className="so-textarea w-full rounded-lg p-2 text-sm"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-xs so-text-stone mb-1">
                  Fixed Discount (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={
                    discountPercent > 0
                      ? totals.discountAmount
                      : discountAmountInput
                  }
                  onChange={(e) => {
                    setDiscountPercent(0);
                    setDiscountAmountInput(Math.max(0, toNumber(e.target.value)));
                  }}
                  className="so-textarea w-full rounded-lg p-2 text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-between text-sm so-text-ink">
            <span>Sub-total</span>

            <span>
              {formatCurrency(
                totals.subtotal
              )}
            </span>
          </div>

          {gstEnabled &&
            totals.taxAmount > 0 && (
              <div className="flex justify-between text-sm mt-2 so-text-ink">
                <span>
                  GST
                  {businessDefaultTaxRate > 0
                    ? ` (${businessDefaultTaxRate}%)`
                    : ""}
                </span>

                <span>
                  {formatCurrency(
                    totals.taxAmount
                  )}
                </span>
              </div>
            )}

          {totals.discountAmount > 0 && (
            <div className="flex justify-between text-sm mt-2" style={{ color: "var(--herb-dark)" }}>
              <span>
                Discount
                {totals.discountPercent > 0
                  ? ` (${totals.discountPercent}%)`
                  : ""}
              </span>
              <span>-{formatCurrency(totals.discountAmount)}</span>
            </div>
          )}

          <div className="flex justify-between items-center mt-3">
            <span className="text-xl font-bold so-text-ink">
              Total
            </span>

            <span className="text-2xl font-bold so-text-herb">
              {formatCurrency(
                totals.totalAmount
              )}
            </span>
          </div>

          <div className="rounded-lg p-3 mt-4" style={{ background: "#f7f2e8", border: "1px solid var(--line)" }}>
            <h4 className="font-semibold text-sm so-text-ink mb-2">
              Payment Split
            </h4>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs so-text-stone mb-1">
                  Cash Amount (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={cashAmount}
                  onChange={(e) => setCashAmount(Math.max(0, toNumber(e.target.value)))}
                  className="so-textarea w-full rounded-lg p-2 text-sm"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-xs so-text-stone mb-1">
                  Online Amount (₹)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={onlineAmount}
                  onChange={(e) => setOnlineAmount(Math.max(0, toNumber(e.target.value)))}
                  className="so-textarea w-full rounded-lg p-2 text-sm"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="flex justify-between text-xs so-text-stone mt-3">
              <span>Paid</span>
              <span>{formatCurrency(roundMoney(toNumber(cashAmount) + toNumber(onlineAmount)))}</span>
            </div>
            <div className="flex justify-between text-sm font-bold so-text-ink mt-1">
              <span>Balance Due</span>
              <span>
                {formatCurrency(
                  Math.max(
                    0,
                    roundMoney(
                      totals.totalAmount -
                      toNumber(cashAmount) -
                      toNumber(onlineAmount)
                    )
                  )
                )}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              handleGenerateBill();

              if (mobile) {
                setShowCartDrawer(false);
              }
            }}
            disabled={
              cart.length === 0 ||
              isProcessing
            }
            className="so-btn-primary w-full mt-4 py-3 rounded-lg font-semibold"
          >
            🧾 Generate Bill
          </button>
        </div>
      </>
    );
  };

  return (
    <>
      {/* Scoped SwiftOrder theme — fonts, tokens and ticket styling.
          Same tokens as Login.js / Dashboard.js / Sidebar.js. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-dpos {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --herb: #3f6b4f;
          --herb-dark: #2d4f39;
          --mustard: #f2a93b;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;
          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-mono: 'JetBrains Mono', 'Courier New', monospace;

          background: var(--paper-dim);
          font-family: var(--font-body);
          color: var(--ink);
        }

        .so-dpos * {
          box-sizing: border-box;
        }

        .so-text-ink { color: var(--ink); }
        .so-text-stone { color: var(--stone); }
        .so-text-stone-light { color: var(--stone-light); }
        .so-text-herb { color: var(--herb-dark); }

        .so-page-title {
          font-family: var(--font-display);
          font-size: 1.9rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-badge-direct {
          background: var(--mustard);
          color: #633806;
          font-family: var(--font-mono);
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          padding: 4px 10px;
          border-radius: 999px;
        }

        .so-subtitle {
          font-size: 0.85rem;
          color: var(--stone);
        }

        .so-gst-note {
          font-size: 0.72rem;
          color: var(--herb-dark);
          font-weight: 700;
          font-family: var(--font-mono);
          letter-spacing: 0.02em;
        }

        .so-cart-toggle {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--chili);
          color: var(--paper);
          padding: 10px 14px;
          border-radius: 8px;
          font-weight: 700;
          font-size: 0.88rem;
          box-shadow: 0 8px 18px -8px rgba(228, 87, 46, 0.55);
        }

        .so-panel {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 12px;
          box-shadow: 0 10px 26px -20px rgba(28, 26, 23, 0.3);
        }

        .so-section-title {
          font-weight: 700;
          color: var(--ink);
          font-size: 0.98rem;
        }

        .so-section-sub {
          font-size: 0.74rem;
          color: var(--stone-light);
        }

        .so-type-tag {
          font-family: var(--font-mono);
          font-size: 0.7rem;
          font-weight: 700;
          color: var(--stone);
        }

        .so-type-card {
          border-radius: 10px;
          border: 1.5px solid var(--line);
          padding: 16px;
          text-align: left;
          background: var(--paper);
          transition: border-color 0.15s ease, background 0.15s ease;
        }

        .so-type-card:hover {
          border-color: var(--stone-light);
        }

        .so-type-card-active-herb {
          border-color: var(--herb);
          background: #eaf3ee;
        }

        .so-type-card-active-chili {
          border-color: var(--chili);
          background: #fdece6;
        }

        .so-clear-link {
          font-size: 0.74rem;
          color: var(--chili);
          font-weight: 600;
        }

        .so-clear-link:hover {
          color: var(--chili-dark);
        }

        .so-empty-note {
          background: #fdf3e2;
          border: 1px solid var(--mustard);
          border-radius: 8px;
          padding: 10px 14px;
          font-size: 0.85rem;
          color: #854f0b;
        }

        .so-table-card {
          border-radius: 10px;
          border: 1.5px solid var(--line);
          padding: 12px;
          text-align: center;
          background: var(--paper);
          transition: border-color 0.15s ease, background 0.15s ease;
        }

        .so-table-card-selected {
          border-color: var(--chili);
          background: #fdece6;
        }

        .so-table-card-occupied {
          border-color: #f0ccc4;
          background: #fdf4f1;
          cursor: not-allowed;
        }

        .so-table-card-unavailable {
          border-color: var(--line);
          background: var(--paper-dim);
          cursor: not-allowed;
        }

        .so-table-card:hover:not(:disabled) {
          border-color: var(--chili);
        }

        .so-selected-table-banner {
          background: #fdece6;
          border: 1px solid var(--chili);
          border-radius: 12px;
          padding: 16px;
        }

        .so-dine-badge {
          background: var(--chili);
          color: var(--paper);
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 0.85rem;
          font-weight: 700;
          font-family: var(--font-mono);
        }

        .so-category-pill {
          padding: 8px 16px;
          border-radius: 999px;
          white-space: nowrap;
          font-size: 0.85rem;
          font-weight: 600;
          background: #fff;
          color: var(--stone);
          border: 1px solid var(--line);
          transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
        }

        .so-category-pill:hover {
          border-color: var(--chili);
        }

        .so-category-pill-active {
          background: var(--ink);
          color: var(--paper);
          border-color: var(--ink);
        }

        .so-item-card {
          background: #fffdf8;
          border-radius: 12px;
          border: 1px solid var(--line);
          overflow: hidden;
          text-align: left;
          transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
        }

        .so-item-card:hover {
          border-color: var(--chili);
          transform: translateY(-2px);
          box-shadow: 0 14px 24px -18px rgba(28, 26, 23, 0.4);
        }

        .so-no-image {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--paper-dim);
        }

        .so-item-price {
          font-weight: 700;
          color: var(--herb-dark);
        }

        .so-add-icon {
          background: var(--chili);
          color: var(--paper);
          border-radius: 999px;
          padding: 6px;
          display: inline-flex;
        }

        .so-item-gst {
          font-size: 0.7rem;
          color: var(--stone-light);
        }

        .so-cart-aside {
          background: #fffdf8;
          box-shadow: -8px 0 24px -20px rgba(28, 26, 23, 0.4);
          border-left: 1px solid var(--line);
        }

        .so-cart-summary {
          background: var(--paper-dim);
          border-radius: 10px;
          padding: 12px;
        }

        .so-cart-line {
          background: var(--paper-dim);
        }

        .so-remove-btn {
          color: var(--chili);
        }

        .so-remove-btn:hover {
          color: var(--chili-dark);
        }

        .so-qty-btn {
          width: 32px;
          height: 32px;
          background: #fff;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--line);
          color: var(--ink);
        }

        .so-qty-btn:hover {
          background: var(--paper-dim);
        }

        .so-textarea {
          border: 1px solid var(--line);
          background: var(--paper);
          color: var(--ink);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .so-textarea:focus {
          border-color: var(--chili);
          box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
        }

        .so-dashed-divider {
          border-top: 1px dashed var(--line);
        }

        .so-btn-primary {
          background: var(--chili);
          color: var(--paper);
          box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
          transition: background 0.15s ease, transform 0.1s ease, opacity 0.15s ease;
        }

        .so-btn-primary:hover:not(:disabled) {
          background: var(--chili-dark);
        }

        .so-btn-primary:disabled {
          background: var(--stone-light);
          cursor: not-allowed;
          box-shadow: none;
        }

        .so-drawer {
          background: #fffdf8;
        }

        .so-drawer-close {
          color: var(--stone);
        }

        .so-drawer-close:hover {
          background: var(--paper-dim);
        }

        .so-invoice-overlay {
          background: rgba(28, 26, 23, 0.6);
        }

        .so-invoice-card {
          background: #fffdf8;
          border-radius: 12px;
        }

        .so-invoice-title {
          font-family: var(--font-display);
          font-size: 1.3rem;
          color: var(--ink);
        }

        .so-invoice-gstin {
          color: var(--ink);
        }

        .so-invoice-total {
          color: var(--herb-dark);
        }

        .so-invoice-btn-outline {
          border: 1px solid var(--line);
          color: var(--ink);
          background: #fff;
          transition: background 0.15s ease;
        }

        .so-invoice-btn-outline:hover:not(:disabled) {
          background: var(--paper-dim);
        }

        .so-invoice-btn-print {
          background: var(--herb);
          color: var(--paper);
        }

        .so-invoice-btn-print:hover:not(:disabled) {
          background: var(--herb-dark);
        }

        .so-invoice-btn-pdf {
          background: var(--ink);
          color: var(--paper);
        }

        .so-invoice-btn-pdf:hover:not(:disabled) {
          background: #302c27;
        }

        .so-invoice-btn-done {
          background: var(--chili);
          color: var(--paper);
          box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
        }

        .so-invoice-btn-done:hover:not(:disabled) {
          background: var(--chili-dark);
        }

        .so-invoice-btn-done:disabled {
          background: var(--stone-light);
        }
      `}</style>

      <div className="so-dpos flex flex-col md:flex-row h-full">

        <div className="flex-1 p-4 md:p-6 overflow-auto">

          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="so-page-title">
                  Direct POS
                </h2>

                <span className="so-badge-direct">
                  DIRECT
                </span>
              </div>

              <p className="so-subtitle mt-1">
                Fast billing without the kitchen queue
              </p>

              {gstEnabled && (
                <p className="so-gst-note mt-1">
                  GST Enabled • GST added on top • Default GST {businessDefaultTaxRate}%
                </p>
              )}
            </div>

            {isMobile && (
              <button
                type="button"
                onClick={() =>
                  setShowCartDrawer(true)
                }
                className="so-cart-toggle"
              >
                <ShoppingCart size={18} />

                <span>
                  Cart ({cart.length})
                </span>
              </button>
            )}
          </div>

          <div className="so-panel p-4 mb-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="so-section-title">
                  Order Type
                </h3>

                <p className="so-section-sub">
                  Select how this customer is ordering
                </p>
              </div>

              <span className="so-type-tag">
                {formatOrderType(orderType)}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() =>
                  handleOrderTypeChange(
                    "TAKEAWAY"
                  )
                }
                className={`so-type-card ${
                  orderType === "TAKEAWAY"
                    ? "so-type-card-active-herb"
                    : ""
                }`}
              >
                <Package
                  size={25}
                  color={
                    orderType === "TAKEAWAY"
                      ? "#2d4f39"
                      : "#a49c8d"
                  }
                />

                <div className="font-bold so-text-ink mt-2">
                  Takeaway
                </div>

                <div className="so-section-sub mt-1">
                  Customer takes the order
                </div>
              </button>

              <button
                type="button"
                onClick={() =>
                  handleOrderTypeChange(
                    "DINE_IN"
                  )
                }
                disabled={orderChannel !== "POS"}
                className={`so-type-card ${
                  orderType === "DINE_IN"
                    ? "so-type-card-active-chili"
                    : ""
                } ${orderChannel !== "POS" ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <Utensils
                  size={25}
                  color={
                    orderType === "DINE_IN"
                      ? "#c43f1a"
                      : "#a49c8d"
                  }
                />

                <div className="font-bold so-text-ink mt-2">
                  Dine In
                </div>

                <div className="so-section-sub mt-1">
                  Customer eats at restaurant
                </div>
              </button>
            </div>
          </div>

          {/* Order channel: walk-in POS, Swiggy or Zomato */}
          <div className="so-panel p-4 mb-5">
            <div className="mb-3">
              <h3 className="so-section-title">Order Channel</h3>
              <p className="so-section-sub">
                Record where this order came from
              </p>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {[
                { value: "POS", label: "Direct POS" },
                { value: "SWIGGY", label: "Swiggy" },
                { value: "ZOMATO", label: "Zomato" },
              ].map((channel) => (
                <button
                  key={channel.value}
                  type="button"
                  onClick={() => handleOrderChannelChange(channel.value)}
                  className={`so-type-card ${
                    orderChannel === channel.value
                      ? "so-type-card-active-chili"
                      : ""
                  }`}
                >
                  <div className="font-bold so-text-ink">
                    {channel.label}
                  </div>
                  <div className="so-section-sub mt-1">
                    {channel.value === "POS"
                      ? "Walk-in / counter"
                      : "Marketplace order"}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {orderType === "DINE_IN" && (
            <div className="so-panel p-4 mb-5">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="so-section-title">
                    Select Table
                  </h3>

                  <p className="so-section-sub">
                    Choose an available table
                  </p>
                </div>

                {selectedTable && (
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedTable(null)
                    }
                    className="so-clear-link"
                  >
                    Clear
                  </button>
                )}
              </div>

              {loadingTables ? (
                <div className="text-center py-6 so-text-stone">
                  Loading tables...
                </div>
              ) : tables.length === 0 ? (
                <div className="so-empty-note">
                  No restaurant tables found.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {tables
                    .filter(
                      (table) =>
                        table.active !== false
                    )
                    .map((table) => {
                      const selected =
                        selectedTable?.id ===
                        table.id;

                      const occupied =
                        table.status ===
                        "OCCUPIED";

                      const unavailable =
                        table.status ===
                        "OUT_OF_SERVICE";

                      return (
                        <button
                          key={table.id}
                          type="button"
                          disabled={
                            occupied ||
                            unavailable
                          }
                          onClick={() =>
                            handleTableSelect(
                              table
                            )
                          }
                          className={`so-table-card ${
                            selected
                              ? "so-table-card-selected"
                              : occupied
                              ? "so-table-card-occupied"
                              : unavailable
                              ? "so-table-card-unavailable"
                              : ""
                          }`}
                        >
                          <div className="font-bold so-text-ink">
                            {table.tableName ||
                              `Table ${table.id}`}
                          </div>

                          <div className="so-section-sub mt-1">
                            Capacity:{" "}
                            {table.capacity ||
                              "-"}
                          </div>

                          <div className="text-xs font-semibold mt-2 so-text-stone">
                            {selected
                              ? "Selected"
                              : occupied
                              ? "Occupied"
                              : unavailable
                              ? "Out of Service"
                              : "Available"}
                          </div>
                        </button>
                      );
                    })}
                </div>
              )}

              {orderType === "DINE_IN" &&
                !selectedTable && (
                  <p className="text-xs mt-3" style={{ color: "var(--chili-dark)" }}>
                    Please select a table.
                  </p>
                )}
            </div>
          )}

          {orderType === "DINE_IN" &&
            selectedTable && (
              <div className="so-selected-table-banner mb-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="so-type-tag" style={{ color: "var(--chili-dark)" }}>
                      SELECTED TABLE
                    </p>

                    <p className="text-xl font-bold mt-1" style={{ color: "var(--chili-dark)" }}>
                      🍽️{" "}
                      {selectedTable.tableName}
                    </p>

                    <p className="text-xs mt-1" style={{ color: "var(--chili-dark)" }}>
                      Capacity:{" "}
                      {selectedTable.capacity}
                    </p>
                  </div>

                  <div className="so-dine-badge">
                    DINE IN
                  </div>
                </div>
              </div>
            )}

          <div className="flex gap-2 mb-5 overflow-x-auto pb-2">
            <button
              type="button"
              onClick={() =>
                setSelectedCategory("all")
              }
              className={`so-category-pill ${
                selectedCategory === "all"
                  ? "so-category-pill-active"
                  : ""
              }`}
            >
              All Items
            </button>

            {categories.map((category) => (
              <button
                type="button"
                key={category.id}
                onClick={() =>
                  setSelectedCategory(
                    category.id
                  )
                }
                className={`so-category-pill ${
                  String(
                    selectedCategory
                  ) === String(category.id)
                    ? "so-category-pill-active"
                    : ""
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {loadingMenu ? (
            <div className="text-center py-12 so-text-stone">
              Loading menu...
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 so-text-stone">
              No menu items available.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {filteredItems.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  onClick={() =>
                    addToCart(item)
                  }
                  className="so-item-card"
                >
                  <div className="h-28 md:h-32 overflow-hidden flex items-center justify-center">
                    <img
                      src={`${API_BASE_URL}/api/menu-items/${item.id}/image`}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(event) => {
                        if (item.imageUrl) {
                          event.currentTarget.src =
                            item.imageUrl;
                        } else {
                          event.currentTarget.style.display =
                            "none";

                          const parent =
                            event.currentTarget
                              .parentElement;

                          if (parent) {
                            parent.innerHTML =
                              PLATE_SPOON_SVG_MARKUP;
                          }
                        }
                      }}
                    />
                  </div>

                  <div className="p-3">
                    <h3 className="font-semibold so-text-ink text-sm md:text-base line-clamp-2">
                      {item.name}
                    </h3>

                    <div className="flex justify-between items-center mt-3">
                      <span className="so-item-price">
                        {formatCurrency(
                          item.price
                        )}
                      </span>

                      <span className="so-add-icon">
                        <Plus size={15} />
                      </span>
                    </div>

                    {gstEnabled && (
                      <div className="so-item-gst mt-1">
                        GST{" "}
                        {getItemTaxRate(item)}%
                        extra
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <aside className="so-cart-aside hidden md:flex md:w-96 p-5 flex-col">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <ShoppingCart
                size={24}
                color="#1c1a17"
              />

              <h3 className="so-page-title" style={{ fontSize: "1.5rem" }}>
                Direct Bill
              </h3>
            </div>

            <span className="so-badge-direct">
              DIRECT
            </span>
          </div>

          <div className="so-cart-summary mb-4">
            <div className="text-xs so-text-stone-light">
              Order Type
            </div>

            <div className="font-bold so-text-ink mt-1">
              {formatOrderType(orderType)}
            </div>

            <div className="text-xs so-text-stone mt-1">
              Channel: {formatOrderChannel(orderChannel)}
            </div>

            {orderType === "DINE_IN" &&
              selectedTable && (
                <div className="text-xs so-text-stone mt-1">
                  Table:{" "}
                  {selectedTable.tableName}
                </div>
              )}
          </div>

          <div className="flex-1 overflow-auto">
            {renderCart()}
          </div>
        </aside>
      </div>

      {isMobile &&
        showCartDrawer && (
          <div className="fixed inset-0 z-50">
            <div
              className="absolute inset-0 so-invoice-overlay"
              onClick={() =>
                setShowCartDrawer(false)
              }
            />

            <div className="so-drawer absolute bottom-0 left-0 right-0 rounded-t-2xl max-h-[90vh] overflow-auto p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <ShoppingCart size={20} color="#1c1a17" />

                  <h3 className="text-xl font-bold so-text-ink">
                    Direct Bill
                  </h3>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    setShowCartDrawer(false)
                  }
                  className="so-drawer-close p-2 rounded-lg"
                >
                  <X size={20} />
                </button>
              </div>

              {renderCart({ mobile: true })}
            </div>
          </div>
        )}

      {showInvoice &&
        invoiceData && (
          <div className="fixed inset-0 z-[60] so-invoice-overlay flex items-center justify-center px-3">
            <div className="so-invoice-card shadow-2xl w-full max-w-md max-h-[95vh] overflow-auto p-4 md:p-6">

              <div
                ref={receiptRef}
                style={{
                  background: "white",
                  paddingBottom: 60,
                }}
              >
                <div className="text-center mb-4">
                  {logoUrl && (
                    <img
                      src={logoUrl}
                      alt="Business Logo"
                      className="h-14 mx-auto mb-2 object-contain"
                    />
                  )}

                  <h2 className="so-invoice-title">
                    {businessProfile?.businessName ||
                      "Restaurant POS"}
                  </h2>

                  {businessProfile?.addressLine1 && (
                    <p className="text-xs so-text-stone mt-1">
                      {
                        businessProfile.addressLine1
                      }
                    </p>
                  )}

                  {(businessProfile?.city ||
                    businessProfile?.state ||
                    businessProfile?.pinCode) && (
                    <p className="text-xs so-text-stone">
                      {[
                        businessProfile?.city,
                        businessProfile?.state,
                        businessProfile?.pinCode,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  )}

                  {businessProfile?.phone && (
                    <p className="text-xs so-text-stone">
                      Tel:{" "}
                      {businessProfile.phone}
                    </p>
                  )}

                  {businessProfile?.gstin && (
                    <p className="text-xs font-semibold so-invoice-gstin mt-1">
                      GSTIN:{" "}
                      {businessProfile.gstin}
                    </p>
                  )}
                </div>

                <div className="so-dashed-divider pt-3">
                  <div className="flex justify-between text-sm so-text-ink">
                    <span className="font-semibold">
                      Order
                    </span>

                    <span>
                      {invoiceData.orderNumber}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm mt-1 so-text-ink">
                    <span className="font-semibold">
                      Type
                    </span>

                    <span>
                      {formatOrderType(invoiceData.orderType)}
                    </span>
                  </div>

                  <div className="flex justify-between text-sm mt-1 so-text-ink">
                    <span className="font-semibold">Channel</span>
                    <span>{formatOrderChannel(invoiceData.orderChannel)}</span>
                  </div>

                  {invoiceData.orderType ===
                    "DINE_IN" &&
                    invoiceData.table && (
                      <div className="flex justify-between text-sm mt-1 so-text-ink">
                        <span className="font-semibold">
                          Table
                        </span>

                        <span>
                          {
                            invoiceData.table
                              .tableName
                          }
                        </span>
                      </div>
                    )}

                  <div className="flex justify-between text-xs so-text-stone mt-1">
                    <span>Date</span>

                    <span>
                      {formatDateTime(
                        invoiceData.createdAt
                      )}
                    </span>
                  </div>
                </div>

                <div className="so-dashed-divider my-3 pt-3">
                  {invoiceData.items.map(
                    (item) => (
                      <div
                        key={item.id}
                        className="mb-3"
                      >
                        <div className="flex justify-between text-sm so-text-ink">
                          <div>
                            <span>
                              {item.name}
                            </span>

                            <span className="text-xs so-text-stone ml-2">
                              ×{" "}
                              {item.quantity}
                            </span>
                          </div>

                          <span className="font-medium">
                            {formatCurrency(
                              item.subtotal
                            )}
                          </span>
                        </div>

                        {gstEnabled &&
                          item.taxRate > 0 && (
                            <div className="flex justify-between text-xs so-text-stone mt-1">
                              <span>
                                GST{" "}
                                {item.taxRate}%
                              </span>

                              <span>
                                +
                                {formatCurrency(
                                  item.taxAmount
                                )}
                              </span>
                            </div>
                          )}

                        <div className="flex justify-between text-xs font-semibold mt-1 so-text-ink">
                          <span>
                            Item Total
                          </span>

                          <span>
                            {formatCurrency(
                              item.lineTotal
                            )}
                          </span>
                        </div>
                      </div>
                    )
                  )}
                </div>

                <div className="so-dashed-divider pt-3">
                  <div className="flex justify-between text-sm so-text-ink">
                    <span>
                      Sub-total
                    </span>

                    <span>
                      {formatCurrency(
                        invoiceData.subtotal
                      )}
                    </span>
                  </div>

                  {invoiceData.gstEnabled &&
                    invoiceData.taxAmount > 0 && (
                      <div className="flex justify-between text-sm mt-2 so-text-ink">
                        <span>
                          GST
                        </span>

                        <span>
                          {formatCurrency(
                            invoiceData.taxAmount
                          )}
                        </span>
                      </div>
                    )}

                  {invoiceData.discountAmount > 0 && (
                    <div className="flex justify-between text-sm mt-2" style={{ color: "#2d4f39" }}>
                      <span>
                        Discount
                        {invoiceData.discountPercent > 0
                          ? ` (${invoiceData.discountPercent}%)`
                          : ""}
                      </span>
                      <span>-{formatCurrency(invoiceData.discountAmount)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-lg font-bold mt-3 so-text-ink">
                    <span>TOTAL</span>

                    <span className="so-invoice-total">
                      {formatCurrency(
                        invoiceData.totalAmount
                      )}
                    </span>
                  </div>

                  <div className="mt-3 pt-3 border-t border-dashed">
                    <div className="flex justify-between text-sm so-text-ink">
                      <span>Cash</span>
                      <span>{formatCurrency(invoiceData.cashAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1 so-text-ink">
                      <span>Online</span>
                      <span>{formatCurrency(invoiceData.onlineAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm mt-1 so-text-ink">
                      <span>Total Paid</span>
                      <span>{formatCurrency(invoiceData.paidAmount)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold mt-1 so-text-ink">
                      <span>Balance Due</span>
                      <span>{formatCurrency(invoiceData.balanceDue)}</span>
                    </div>
                    <div className="text-xs so-text-stone mt-1 text-right">
                      {invoiceData.paymentStatus}
                    </div>
                  </div>
                </div>

                {invoiceData.specialInstructions && (
                  <div className="so-dashed-divider mt-3 pt-3">
                    <p className="text-xs so-text-ink">
                      <span className="font-semibold">
                        Notes:
                      </span>{" "}
                      {
                        invoiceData.specialInstructions
                      }
                    </p>
                  </div>
                )}

                <p className="text-center text-sm so-text-stone mt-5">
                  Thank you for your purchase!
                </p>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setShowInvoice(false)
                  }
                  disabled={isProcessing}
                  className="so-invoice-btn-outline rounded-lg py-3 text-sm font-medium"
                >
                  Back
                </button>

                <button
                  type="button"
                  onClick={handlePrint}
                  disabled={isProcessing}
                  className="so-invoice-btn-print rounded-lg py-3 text-sm font-semibold"
                >
                  🖨 Print
                </button>

                <button
                  type="button"
                  onClick={handleDownloadPDF}
                  disabled={isProcessing}
                  className="so-invoice-btn-pdf rounded-lg py-3 text-sm font-semibold"
                >
                  PDF
                </button>
              </div>

              <button
                type="button"
                onClick={handleInvoiceDone}
                disabled={isProcessing}
                className="so-invoice-btn-done w-full mt-3 py-3 rounded-lg font-bold"
              >
                {isProcessing
                  ? "Saving Order..."
                  : "✓ Done & Save Order"}
              </button>

              <p className="text-center text-xs so-text-stone mt-2">
                This order will be saved directly
                to Order History.
              </p>
            </div>
          </div>
        )}
    </>
  );
}









// // src/components/DirectPOSView.js

// import React, { useEffect, useRef, useState } from "react";
// import {
//   ShoppingCart,
//   Plus,
//   Minus,
//   Trash2,
//   Utensils,
//   Package,
//   X,
// } from "lucide-react";

// import {
//   getAvailableMenuItems,
//   getCategories,
//   getTables,
//   createDirectOrder,
//   getBusinessProfile,
//   getBusinessLogoBlob,
// } from "../api";

// import jsPDF from "jspdf";
// import html2canvas from "html2canvas";

// const API_BASE_URL =
//   process.env.REACT_APP_API_URL || "http://localhost:8080";

// // =========================================================
// // DEFAULT "NO IMAGE" ILLUSTRATION — plate & spoon, SwiftOrder style
// // Used both as a React fallback element and, as a raw string,
// // inside the onError innerHTML swap (img tags can't onError-recover
// // to JSX, only to markup).
// // =========================================================

// const PLATE_SPOON_SVG_MARKUP = `
//   <div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f2ede1;">
//     <svg width="54" height="54" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
//       <circle cx="32" cy="32" r="27" fill="#fbf9f4" stroke="#ddd4c2" stroke-width="2"/>
//       <circle cx="32" cy="32" r="17" fill="none" stroke="#ddd4c2" stroke-width="2"/>
//       <path d="M17 16 L17 30 C17 32.2 18.8 34 21 34 C23.2 34 25 32.2 25 30 L25 16" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
//       <line x1="21" y1="16" x2="21" y2="48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
//       <path d="M45 16 C41 16 39 20 39 25 C39 29 41 31 45 31 L45 48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round" fill="none"/>
//     </svg>
//   </div>
// `;

// function PlateSpoonPlaceholder() {
//   return (
//     <div className="so-no-image">
//       <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
//         <circle cx="32" cy="32" r="27" fill="#fbf9f4" stroke="#ddd4c2" strokeWidth="2" />
//         <circle cx="32" cy="32" r="17" fill="none" stroke="#ddd4c2" strokeWidth="2" />
//         <path d="M17 16 L17 30 C17 32.2 18.8 34 21 34 C23.2 34 25 32.2 25 30 L25 16" stroke="#a49c8d" strokeWidth="2" strokeLinecap="round" />
//         <line x1="21" y1="16" x2="21" y2="48" stroke="#a49c8d" strokeWidth="2" strokeLinecap="round" />
//         <path d="M45 16 C41 16 39 20 39 25 C39 29 41 31 45 31 L45 48" stroke="#a49c8d" strokeWidth="2" strokeLinecap="round" fill="none" />
//       </svg>
//     </div>
//   );
// }

// export default function DirectPOSView() {
//   const [menuItems, setMenuItems] = useState([]);
//   const [categories, setCategories] = useState([]);
//   const [selectedCategory, setSelectedCategory] = useState("all");

//   const [cart, setCart] = useState([]);

//   const [orderType, setOrderType] = useState("TAKEAWAY");
//   const [tables, setTables] = useState([]);
//   const [selectedTable, setSelectedTable] = useState(null);

//   const [specialInstructions, setSpecialInstructions] = useState("");

//   const [businessProfile, setBusinessProfile] = useState(null);
//   const [logoUrl, setLogoUrl] = useState(null);

//   const [showInvoice, setShowInvoice] = useState(false);
//   const [invoiceData, setInvoiceData] = useState(null);

//   const [isProcessing, setIsProcessing] = useState(false);
//   const [loadingMenu, setLoadingMenu] = useState(true);
//   const [loadingTables, setLoadingTables] = useState(true);

//   const [showCartDrawer, setShowCartDrawer] = useState(false);
//   const [isMobile, setIsMobile] = useState(
//     typeof window !== "undefined" ? window.innerWidth < 768 : false
//   );

//   const receiptRef = useRef(null);

//   useEffect(() => {
//     const handleResize = () => {
//       setIsMobile(window.innerWidth < 768);
//     };

//     window.addEventListener("resize", handleResize);

//     return () => {
//       window.removeEventListener("resize", handleResize);
//     };
//   }, []);

//   useEffect(() => {
//     loadMenu();
//     loadCategories();
//     loadTables();
//     loadBusinessProfile();
//   }, []);

//   const loadMenu = async () => {
//     try {
//       setLoadingMenu(true);

//       const data = await getAvailableMenuItems();

//       setMenuItems(Array.isArray(data) ? data : []);
//     } catch (error) {
//       console.error("Failed to load menu:", error);
//       alert("Unable to load menu items.");
//     } finally {
//       setLoadingMenu(false);
//     }
//   };

//   const loadCategories = async () => {
//     try {
//       const data = await getCategories();

//       setCategories(Array.isArray(data) ? data : []);
//     } catch (error) {
//       console.error("Failed to load categories:", error);
//     }
//   };

//   const loadTables = async () => {
//     try {
//       setLoadingTables(true);

//       const data = await getTables();

//       setTables(Array.isArray(data) ? data : []);
//     } catch (error) {
//       console.error("Failed to load tables:", error);
//       setTables([]);
//     } finally {
//       setLoadingTables(false);
//     }
//   };

//   const loadBusinessProfile = async () => {
//     try {
//       const data = await getBusinessProfile();

//       if (!data) return;

//       setBusinessProfile(data);

//       if (data.hasLogo) {
//         try {
//           const response = await getBusinessLogoBlob();

//           const url = URL.createObjectURL(response.data);

//           setLogoUrl(url);
//         } catch (error) {
//           console.error("Failed to load business logo:", error);
//         }
//       }
//     } catch (error) {
//       console.error("Failed to load business profile:", error);
//     }
//   };

//   useEffect(() => {
//     return () => {
//       if (logoUrl) {
//         URL.revokeObjectURL(logoUrl);
//       }
//     };
//   }, [logoUrl]);

//   const toNumber = (value) => {
//     const number = Number(value);

//     return Number.isFinite(number) ? number : 0;
//   };

//   const roundMoney = (value) => {
//     return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
//   };

//   const gstEnabled = Boolean(businessProfile?.gstEnabled);

//   const businessDefaultTaxRate = gstEnabled
//     ? toNumber(businessProfile?.defaultTaxRate)
//     : 0;

//   const getItemTaxRate = (menuItem) => {
//     const itemRate = toNumber(menuItem?.taxRate);

//     if (itemRate > 0) {
//       return itemRate;
//     }

//     return businessDefaultTaxRate;
//   };

//   /*
//    * DIRECT POS GST IS ALWAYS EXTRA.
//    *
//    * ₹500 + 5% GST = ₹525
//    * ₹500 + 3% GST = ₹515
//    */
//   const calculateLine = (menuItem, quantity) => {
//     const price = roundMoney(menuItem?.price);

//     const qty = Math.max(
//       0,
//       Math.floor(toNumber(quantity))
//     );

//     const subtotal = roundMoney(price * qty);

//     if (!gstEnabled) {
//       return {
//         subtotal,
//         taxRate: 0,
//         taxAmount: 0,
//         total: subtotal,
//       };
//     }

//     const taxRate = roundMoney(
//       getItemTaxRate(menuItem)
//     );

//     if (taxRate <= 0) {
//       return {
//         subtotal,
//         taxRate: 0,
//         taxAmount: 0,
//         total: subtotal,
//       };
//     }

//     const taxAmount = roundMoney(
//       subtotal * taxRate / 100
//     );

//     const total = roundMoney(
//       subtotal + taxAmount
//     );

//     return {
//       subtotal,
//       taxRate,
//       taxAmount,
//       total,
//     };
//   };

//   const calculateCartTotals = () => {
//     let subtotal = 0;
//     let taxAmount = 0;
//     let totalAmount = 0;

//     cart.forEach((item) => {
//       const line = calculateLine(
//         item.menuItem,
//         item.quantity
//       );

//       subtotal = roundMoney(
//         subtotal + line.subtotal
//       );

//       taxAmount = roundMoney(
//         taxAmount + line.taxAmount
//       );

//       totalAmount = roundMoney(
//         totalAmount + line.total
//       );
//     });

//     return {
//       subtotal,
//       taxAmount,
//       totalAmount,
//     };
//   };

//   const filteredItems =
//     selectedCategory === "all"
//       ? menuItems
//       : menuItems.filter(
//           (item) =>
//             item.category &&
//             String(item.category.id) ===
//               String(selectedCategory)
//         );

//   const addToCart = (menuItem) => {
//     setCart((previousCart) => {
//       const existing = previousCart.find(
//         (item) =>
//           String(item.menuItem.id) ===
//           String(menuItem.id)
//       );

//       if (existing) {
//         return previousCart.map((item) =>
//           String(item.menuItem.id) ===
//           String(menuItem.id)
//             ? {
//                 ...item,
//                 quantity: item.quantity + 1,
//               }
//             : item
//         );
//       }

//       return [
//         ...previousCart,
//         {
//           menuItem,
//           quantity: 1,
//         },
//       ];
//     });
//   };

//   const updateQuantity = (menuItemId, change) => {
//     setCart((previousCart) =>
//       previousCart
//         .map((item) =>
//           String(item.menuItem.id) ===
//           String(menuItemId)
//             ? {
//                 ...item,
//                 quantity:
//                   item.quantity + change,
//               }
//             : item
//         )
//         .filter((item) => item.quantity > 0)
//     );
//   };

//   const removeFromCart = (menuItemId) => {
//     setCart((previousCart) =>
//       previousCart.filter(
//         (item) =>
//           String(item.menuItem.id) !==
//           String(menuItemId)
//       )
//     );
//   };

//   const getTotalItems = () => {
//     return cart.reduce(
//       (total, item) =>
//         total + item.quantity,
//       0
//     );
//   };

//   const handleOrderTypeChange = (type) => {
//     setOrderType(type);

//     if (type === "TAKEAWAY") {
//       setSelectedTable(null);
//     }
//   };

//   const handleTableSelect = (table) => {
//     if (!table) return;

//     if (table.status === "OCCUPIED") {
//       alert(
//         `${table.tableName} is currently occupied.`
//       );
//       return;
//     }

//     if (table.status === "OUT_OF_SERVICE") {
//       alert(
//         `${table.tableName} is out of service.`
//       );
//       return;
//     }

//     setSelectedTable(table);
//   };

//   const generateOrderNumber = () => {
//     return `POS-${Date.now()}`;
//   };

//   const formatCurrency = (value) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     }).format(toNumber(value));
//   };

//   const formatDateTime = (value) => {
//     if (!value) return "";

//     return new Date(value).toLocaleString("en-IN");
//   };

//   const formatOrderType = (type) => {
//     return type === "DINE_IN"
//       ? "🍽️ DINE IN"
//       : "🥡 TAKEAWAY";
//   };

//   const handleGenerateBill = () => {
//     if (cart.length === 0) {
//       alert("Please add at least one item.");
//       return;
//     }

//     if (
//       orderType === "DINE_IN" &&
//       !selectedTable
//     ) {
//       alert("Please select a table for Dine In.");
//       return;
//     }

//     const orderNumber = generateOrderNumber();

//     const totals = calculateCartTotals();

//     const invoiceItems = cart.map((item) => {
//       const line = calculateLine(
//         item.menuItem,
//         item.quantity
//       );

//       return {
//         id: item.menuItem.id,
//         name: item.menuItem.name,
//         price: roundMoney(item.menuItem.price),
//         quantity: item.quantity,
//         taxRate: line.taxRate,
//         taxAmount: line.taxAmount,
//         subtotal: line.subtotal,
//         lineTotal: line.total,
//       };
//     });

//     setInvoiceData({
//       orderNumber,
//       orderType,
//       table: selectedTable,
//       specialInstructions,
//       createdAt: new Date().toISOString(),

//       gstEnabled,

//       taxInclusive: false,

//       defaultTaxRate:
//         businessDefaultTaxRate,

//       subtotal: totals.subtotal,
//       taxAmount: totals.taxAmount,
//       totalAmount: totals.totalAmount,

//       items: invoiceItems,
//     });

//     setShowInvoice(true);
//   };

//   const handleInvoiceDone = async () => {
//     if (!invoiceData) return;

//     setIsProcessing(true);

//     try {
//       const payload = {
//         orderNumber:
//           invoiceData.orderNumber,

//         orderType:
//           invoiceData.orderType,

//         tableId:
//           invoiceData.orderType === "DINE_IN"
//             ? invoiceData.table?.id
//             : null,

//         specialInstructions:
//           invoiceData.specialInstructions || null,

//         items: invoiceData.items.map(
//           (item) => ({
//             menuItemId: item.id,
//             quantity: item.quantity,

//             /*
//              * IMPORTANT:
//              * Send ORIGINAL menu price.
//              *
//              * ₹500 is sent.
//              * Backend calculates:
//              * GST ₹25
//              * Total ₹525
//              */
//             price: item.price,

//             specialInstructions: null,

//             taxRate: item.taxRate,
//           })
//         ),
//       };

//       const savedOrder =
//         await createDirectOrder(payload);

//       console.log(
//         "DIRECT ORDER SAVED:",
//         savedOrder
//       );

//       setCart([]);
//       setOrderType("TAKEAWAY");
//       setSelectedTable(null);
//       setSpecialInstructions("");
//       setInvoiceData(null);
//       setShowInvoice(false);
//       setShowCartDrawer(false);

//       await loadTables();

//       alert("Order saved successfully.");
//     } catch (error) {
//       console.error(
//         "Direct POS order failed:",
//         error
//       );

//       console.error(
//         "Server response:",
//         error?.response?.data
//       );

//       const serverMessage =
//         error?.response?.data;

//       alert(
//         typeof serverMessage === "string"
//           ? serverMessage
//           : serverMessage?.message ||
//             "Unable to save order."
//       );
//     } finally {
//       setIsProcessing(false);
//     }
//   };

//   const handlePrint = () => {
//     if (!receiptRef.current) return;

//     const content =
//       receiptRef.current.innerHTML;

//     const printWindow = window.open(
//       "",
//       "_blank",
//       "width=420,height=750"
//     );

//     if (!printWindow) {
//       alert(
//         "Popup blocked. Please allow popups to print."
//       );
//       return;
//     }

//     printWindow.document.open();

//     printWindow.document.write(`
//       <html>
//         <head>
//           <title>Invoice</title>
//           <meta
//             name="viewport"
//             content="width=device-width, initial-scale=1"
//           />

//           <style>
//             * {
//               box-sizing: border-box;
//             }

//             body {
//               font-family: Arial, Helvetica, sans-serif;
//               margin: 0;
//               padding: 20px;
//               background: white;
//               color: #111827;
//             }

//             .row {
//               display: flex;
//               justify-content: space-between;
//               gap: 10px;
//             }

//             img {
//               max-width: 120px;
//               max-height: 70px;
//               object-fit: contain;
//             }
//           </style>
//         </head>

//         <body>
//           ${content}
//         </body>
//       </html>
//     `);

//     printWindow.document.close();
//     printWindow.focus();

//     setTimeout(() => {
//       printWindow.print();
//       printWindow.close();
//     }, 400);
//   };

//   const handleDownloadPDF = async () => {
//     if (
//       !receiptRef.current ||
//       !invoiceData
//     ) {
//       return;
//     }

//     const element = receiptRef.current;

//     const originalWidth =
//       element.style.width;

//     const originalMaxHeight =
//       element.style.maxHeight;

//     const originalPaddingBottom =
//       element.style.paddingBottom;

//     try {
//       const targetWidth =
//         window.innerWidth > 900
//           ? 700
//           : Math.floor(
//               window.innerWidth * 0.92
//             );

//       element.style.width =
//         `${targetWidth}px`;

//       element.style.maxHeight = "none";
//       element.style.paddingBottom = "60px";

//       const canvas =
//         await html2canvas(
//           element,
//           {
//             scale:
//               Math.min(
//                 2,
//                 window.devicePixelRatio ||
//                   1.5
//               ) * 1.2,

//             useCORS: true,

//             scrollX: 0,
//             scrollY: -window.scrollY,

//             windowWidth:
//               element.scrollWidth,

//             windowHeight:
//               element.scrollHeight,
//           }
//         );

//       const imageData =
//         canvas.toDataURL("image/png");

//       const pdf =
//         new jsPDF(
//           "p",
//           "pt",
//           "a4"
//         );

//       const pageWidth =
//         pdf.internal.pageSize.getWidth();

//       const pageHeight =
//         pdf.internal.pageSize.getHeight();

//       const margin = 20;

//       const imageWidth =
//         pageWidth - margin * 2;

//       const imageHeight =
//         (canvas.height *
//           imageWidth) /
//         canvas.width;

//       let heightLeft =
//         imageHeight;

//       let position = margin;

//       pdf.addImage(
//         imageData,
//         "PNG",
//         margin,
//         position,
//         imageWidth,
//         imageHeight
//       );

//       heightLeft -=
//         pageHeight - margin * 2;

//       while (heightLeft > 0) {
//         pdf.addPage();

//         position =
//           margin -
//           (
//             imageHeight -
//             heightLeft
//           );

//         pdf.addImage(
//           imageData,
//           "PNG",
//           margin,
//           position,
//           imageWidth,
//           imageHeight
//         );

//         heightLeft -=
//           pageHeight - margin * 2;
//       }

//       pdf.save(
//         `invoice_${invoiceData.orderNumber}.pdf`
//       );
//     } catch (error) {
//       console.error(
//         "PDF generation failed:",
//         error
//       );

//       alert(
//         "Failed to generate PDF."
//       );
//     } finally {
//       element.style.width =
//         originalWidth || "";

//       element.style.maxHeight =
//         originalMaxHeight || "";

//       element.style.paddingBottom =
//         originalPaddingBottom || "";
//     }
//   };

//   /*
//    * This is a plain render function, called as renderCart() inside JSX.
//    * Do not turn it back into a nested component: a component defined
//    * inside another component gets a new identity on every render, so
//    * React remounts it on each keystroke. That made the Order Notes
//    * textarea lose focus (and the keyboard close) after every letter.
//    */
//   const renderCart = ({ mobile = false } = {}) => {
//     const totals =
//       calculateCartTotals();

//     return (
//       <>
//         {cart.length === 0 ? (
//           <div className="text-center py-10 so-text-stone-light">
//             <ShoppingCart
//               size={48}
//               className="mx-auto mb-3 opacity-40"
//             />

//             <p>Cart is empty</p>

//             <p className="text-xs mt-1">
//               Add menu items to continue.
//             </p>
//           </div>
//         ) : (
//           <div className="space-y-3">
//             {cart.map((item) => {
//               const line =
//                 calculateLine(
//                   item.menuItem,
//                   item.quantity
//                 );

//               return (
//                 <div
//                   key={item.menuItem.id}
//                   className="so-cart-line rounded-lg p-3"
//                 >
//                   <div className="flex justify-between items-start">
//                     <div className="pr-2">
//                       <h4 className="font-semibold so-text-ink">
//                         {item.menuItem.name}
//                       </h4>

//                       <p className="text-xs so-text-stone mt-1">
//                         {formatCurrency(
//                           item.menuItem.price
//                         )}{" "}
//                         each
//                       </p>

//                       {gstEnabled &&
//                         line.taxRate > 0 && (
//                           <p className="text-xs so-text-stone mt-1">
//                             GST{" "}
//                             {line.taxRate}%
//                             {" + "}
//                             {formatCurrency(
//                               line.taxAmount
//                             )}
//                           </p>
//                         )}
//                     </div>

//                     <button
//                       type="button"
//                       onClick={() =>
//                         removeFromCart(
//                           item.menuItem.id
//                         )
//                       }
//                       className="so-remove-btn"
//                     >
//                       <Trash2 size={16} />
//                     </button>
//                   </div>

//                   <div className="flex justify-between items-center mt-3">
//                     <div className="flex items-center gap-2">
//                       <button
//                         type="button"
//                         onClick={() =>
//                           updateQuantity(
//                             item.menuItem.id,
//                             -1
//                           )
//                         }
//                         className="so-qty-btn"
//                       >
//                         <Minus size={15} />
//                       </button>

//                       <span className="w-8 text-center font-semibold so-text-ink">
//                         {item.quantity}
//                       </span>

//                       <button
//                         type="button"
//                         onClick={() =>
//                           updateQuantity(
//                             item.menuItem.id,
//                             1
//                           )
//                         }
//                         className="so-qty-btn"
//                       >
//                         <Plus size={15} />
//                       </button>
//                     </div>

//                     <div className="text-right">
//                       <div className="font-bold so-text-ink">
//                         {formatCurrency(
//                           line.total
//                         )}
//                       </div>

//                       {gstEnabled &&
//                         line.taxAmount > 0 && (
//                           <div className="text-xs so-text-stone">
//                             GST{" "}
//                             {formatCurrency(
//                               line.taxAmount
//                             )}
//                           </div>
//                         )}
//                     </div>
//                   </div>
//                 </div>
//               );
//             })}
//           </div>
//         )}

//         <div className="mt-4">
//           <label className="block text-sm font-semibold so-text-ink mb-1">
//             Order Notes
//           </label>

//           <textarea
//             value={specialInstructions}
//             onChange={(e) =>
//               setSpecialInstructions(
//                 e.target.value
//               )
//             }
//             rows={3}
//             maxLength={500}
//             placeholder="e.g. Less spicy, extra sauce, no onion..."
//             className="so-textarea w-full rounded-lg p-3 text-sm"
//           />

//           <div className="text-right text-xs so-text-stone-light mt-1">
//             {specialInstructions.length}/500
//           </div>
//         </div>

//         <div className="so-dashed-divider mt-4 pt-4">
//           <div className="flex justify-between text-sm mb-2">
//             <span className="so-text-stone">
//               {getTotalItems()} item
//               {getTotalItems() !== 1
//                 ? "s"
//                 : ""}
//             </span>
//           </div>

//           <div className="flex justify-between text-sm so-text-ink">
//             <span>Sub-total</span>

//             <span>
//               {formatCurrency(
//                 totals.subtotal
//               )}
//             </span>
//           </div>

//           {gstEnabled &&
//             totals.taxAmount > 0 && (
//               <div className="flex justify-between text-sm mt-2 so-text-ink">
//                 <span>
//                   GST
//                   {businessDefaultTaxRate > 0
//                     ? ` (${businessDefaultTaxRate}%)`
//                     : ""}
//                 </span>

//                 <span>
//                   {formatCurrency(
//                     totals.taxAmount
//                   )}
//                 </span>
//               </div>
//             )}

//           <div className="flex justify-between items-center mt-3">
//             <span className="text-xl font-bold so-text-ink">
//               Total
//             </span>

//             <span className="text-2xl font-bold so-text-herb">
//               {formatCurrency(
//                 totals.totalAmount
//               )}
//             </span>
//           </div>

//           <button
//             type="button"
//             onClick={() => {
//               handleGenerateBill();

//               if (mobile) {
//                 setShowCartDrawer(false);
//               }
//             }}
//             disabled={
//               cart.length === 0 ||
//               isProcessing
//             }
//             className="so-btn-primary w-full mt-4 py-3 rounded-lg font-semibold"
//           >
//             🧾 Generate Bill
//           </button>
//         </div>
//       </>
//     );
//   };

//   return (
//     <>
//       {/* Scoped SwiftOrder theme — fonts, tokens and ticket styling.
//           Same tokens as Login.js / Dashboard.js / Sidebar.js. */}
//       <style>{`
//         @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

//         .so-dpos {
//           --ink: #1c1a17;
//           --paper: #fbf9f4;
//           --paper-dim: #f2ede1;
//           --chili: #e4572e;
//           --chili-dark: #c43f1a;
//           --herb: #3f6b4f;
//           --herb-dark: #2d4f39;
//           --mustard: #f2a93b;
//           --stone: #6b6459;
//           --stone-light: #a49c8d;
//           --line: #ddd4c2;
//           --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
//           --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
//           --font-mono: 'JetBrains Mono', 'Courier New', monospace;

//           background: var(--paper-dim);
//           font-family: var(--font-body);
//           color: var(--ink);
//         }

//         .so-dpos * {
//           box-sizing: border-box;
//         }

//         .so-text-ink { color: var(--ink); }
//         .so-text-stone { color: var(--stone); }
//         .so-text-stone-light { color: var(--stone-light); }
//         .so-text-herb { color: var(--herb-dark); }

//         .so-page-title {
//           font-family: var(--font-display);
//           font-size: 1.9rem;
//           letter-spacing: 0.01em;
//           color: var(--ink);
//         }

//         .so-badge-direct {
//           background: var(--mustard);
//           color: #633806;
//           font-family: var(--font-mono);
//           font-size: 0.68rem;
//           font-weight: 700;
//           letter-spacing: 0.06em;
//           padding: 4px 10px;
//           border-radius: 999px;
//         }

//         .so-subtitle {
//           font-size: 0.85rem;
//           color: var(--stone);
//         }

//         .so-gst-note {
//           font-size: 0.72rem;
//           color: var(--herb-dark);
//           font-weight: 700;
//           font-family: var(--font-mono);
//           letter-spacing: 0.02em;
//         }

//         .so-cart-toggle {
//           display: flex;
//           align-items: center;
//           gap: 8px;
//           background: var(--chili);
//           color: var(--paper);
//           padding: 10px 14px;
//           border-radius: 8px;
//           font-weight: 700;
//           font-size: 0.88rem;
//           box-shadow: 0 8px 18px -8px rgba(228, 87, 46, 0.55);
//         }

//         .so-panel {
//           background: #fffdf8;
//           border: 1px solid var(--line);
//           border-radius: 12px;
//           box-shadow: 0 10px 26px -20px rgba(28, 26, 23, 0.3);
//         }

//         .so-section-title {
//           font-weight: 700;
//           color: var(--ink);
//           font-size: 0.98rem;
//         }

//         .so-section-sub {
//           font-size: 0.74rem;
//           color: var(--stone-light);
//         }

//         .so-type-tag {
//           font-family: var(--font-mono);
//           font-size: 0.7rem;
//           font-weight: 700;
//           color: var(--stone);
//         }

//         .so-type-card {
//           border-radius: 10px;
//           border: 1.5px solid var(--line);
//           padding: 16px;
//           text-align: left;
//           background: var(--paper);
//           transition: border-color 0.15s ease, background 0.15s ease;
//         }

//         .so-type-card:hover {
//           border-color: var(--stone-light);
//         }

//         .so-type-card-active-herb {
//           border-color: var(--herb);
//           background: #eaf3ee;
//         }

//         .so-type-card-active-chili {
//           border-color: var(--chili);
//           background: #fdece6;
//         }

//         .so-clear-link {
//           font-size: 0.74rem;
//           color: var(--chili);
//           font-weight: 600;
//         }

//         .so-clear-link:hover {
//           color: var(--chili-dark);
//         }

//         .so-empty-note {
//           background: #fdf3e2;
//           border: 1px solid var(--mustard);
//           border-radius: 8px;
//           padding: 10px 14px;
//           font-size: 0.85rem;
//           color: #854f0b;
//         }

//         .so-table-card {
//           border-radius: 10px;
//           border: 1.5px solid var(--line);
//           padding: 12px;
//           text-align: center;
//           background: var(--paper);
//           transition: border-color 0.15s ease, background 0.15s ease;
//         }

//         .so-table-card-selected {
//           border-color: var(--chili);
//           background: #fdece6;
//         }

//         .so-table-card-occupied {
//           border-color: #f0ccc4;
//           background: #fdf4f1;
//           cursor: not-allowed;
//         }

//         .so-table-card-unavailable {
//           border-color: var(--line);
//           background: var(--paper-dim);
//           cursor: not-allowed;
//         }

//         .so-table-card:hover:not(:disabled) {
//           border-color: var(--chili);
//         }

//         .so-selected-table-banner {
//           background: #fdece6;
//           border: 1px solid var(--chili);
//           border-radius: 12px;
//           padding: 16px;
//         }

//         .so-dine-badge {
//           background: var(--chili);
//           color: var(--paper);
//           padding: 8px 12px;
//           border-radius: 8px;
//           font-size: 0.85rem;
//           font-weight: 700;
//           font-family: var(--font-mono);
//         }

//         .so-category-pill {
//           padding: 8px 16px;
//           border-radius: 999px;
//           white-space: nowrap;
//           font-size: 0.85rem;
//           font-weight: 600;
//           background: #fff;
//           color: var(--stone);
//           border: 1px solid var(--line);
//           transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
//         }

//         .so-category-pill:hover {
//           border-color: var(--chili);
//         }

//         .so-category-pill-active {
//           background: var(--ink);
//           color: var(--paper);
//           border-color: var(--ink);
//         }

//         .so-item-card {
//           background: #fffdf8;
//           border-radius: 12px;
//           border: 1px solid var(--line);
//           overflow: hidden;
//           text-align: left;
//           transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
//         }

//         .so-item-card:hover {
//           border-color: var(--chili);
//           transform: translateY(-2px);
//           box-shadow: 0 14px 24px -18px rgba(28, 26, 23, 0.4);
//         }

//         .so-no-image {
//           width: 100%;
//           height: 100%;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           background: var(--paper-dim);
//         }

//         .so-item-price {
//           font-weight: 700;
//           color: var(--herb-dark);
//         }

//         .so-add-icon {
//           background: var(--chili);
//           color: var(--paper);
//           border-radius: 999px;
//           padding: 6px;
//           display: inline-flex;
//         }

//         .so-item-gst {
//           font-size: 0.7rem;
//           color: var(--stone-light);
//         }

//         .so-cart-aside {
//           background: #fffdf8;
//           box-shadow: -8px 0 24px -20px rgba(28, 26, 23, 0.4);
//           border-left: 1px solid var(--line);
//         }

//         .so-cart-summary {
//           background: var(--paper-dim);
//           border-radius: 10px;
//           padding: 12px;
//         }

//         .so-cart-line {
//           background: var(--paper-dim);
//         }

//         .so-remove-btn {
//           color: var(--chili);
//         }

//         .so-remove-btn:hover {
//           color: var(--chili-dark);
//         }

//         .so-qty-btn {
//           width: 32px;
//           height: 32px;
//           background: #fff;
//           border-radius: 6px;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           border: 1px solid var(--line);
//           color: var(--ink);
//         }

//         .so-qty-btn:hover {
//           background: var(--paper-dim);
//         }

//         .so-textarea {
//           border: 1px solid var(--line);
//           background: var(--paper);
//           color: var(--ink);
//           outline: none;
//           transition: border-color 0.15s ease, box-shadow 0.15s ease;
//         }

//         .so-textarea:focus {
//           border-color: var(--chili);
//           box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
//         }

//         .so-dashed-divider {
//           border-top: 1px dashed var(--line);
//         }

//         .so-btn-primary {
//           background: var(--chili);
//           color: var(--paper);
//           box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
//           transition: background 0.15s ease, transform 0.1s ease, opacity 0.15s ease;
//         }

//         .so-btn-primary:hover:not(:disabled) {
//           background: var(--chili-dark);
//         }

//         .so-btn-primary:disabled {
//           background: var(--stone-light);
//           cursor: not-allowed;
//           box-shadow: none;
//         }

//         .so-drawer {
//           background: #fffdf8;
//         }

//         .so-drawer-close {
//           color: var(--stone);
//         }

//         .so-drawer-close:hover {
//           background: var(--paper-dim);
//         }

//         .so-invoice-overlay {
//           background: rgba(28, 26, 23, 0.6);
//         }

//         .so-invoice-card {
//           background: #fffdf8;
//           border-radius: 12px;
//         }

//         .so-invoice-title {
//           font-family: var(--font-display);
//           font-size: 1.3rem;
//           color: var(--ink);
//         }

//         .so-invoice-gstin {
//           color: var(--ink);
//         }

//         .so-invoice-total {
//           color: var(--herb-dark);
//         }

//         .so-invoice-btn-outline {
//           border: 1px solid var(--line);
//           color: var(--ink);
//           background: #fff;
//           transition: background 0.15s ease;
//         }

//         .so-invoice-btn-outline:hover:not(:disabled) {
//           background: var(--paper-dim);
//         }

//         .so-invoice-btn-print {
//           background: var(--herb);
//           color: var(--paper);
//         }

//         .so-invoice-btn-print:hover:not(:disabled) {
//           background: var(--herb-dark);
//         }

//         .so-invoice-btn-pdf {
//           background: var(--ink);
//           color: var(--paper);
//         }

//         .so-invoice-btn-pdf:hover:not(:disabled) {
//           background: #302c27;
//         }

//         .so-invoice-btn-done {
//           background: var(--chili);
//           color: var(--paper);
//           box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
//         }

//         .so-invoice-btn-done:hover:not(:disabled) {
//           background: var(--chili-dark);
//         }

//         .so-invoice-btn-done:disabled {
//           background: var(--stone-light);
//         }
//       `}</style>

//       <div className="so-dpos flex flex-col md:flex-row h-full">

//         <div className="flex-1 p-4 md:p-6 overflow-auto">

//           <div className="flex items-center justify-between mb-5">
//             <div>
//               <div className="flex items-center gap-2">
//                 <h2 className="so-page-title">
//                   Direct POS
//                 </h2>

//                 <span className="so-badge-direct">
//                   DIRECT
//                 </span>
//               </div>

//               <p className="so-subtitle mt-1">
//                 Fast billing without the kitchen queue
//               </p>

//               {gstEnabled && (
//                 <p className="so-gst-note mt-1">
//                   GST Enabled • GST added on top • Default GST {businessDefaultTaxRate}%
//                 </p>
//               )}
//             </div>

//             {isMobile && (
//               <button
//                 type="button"
//                 onClick={() =>
//                   setShowCartDrawer(true)
//                 }
//                 className="so-cart-toggle"
//               >
//                 <ShoppingCart size={18} />

//                 <span>
//                   Cart ({cart.length})
//                 </span>
//               </button>
//             )}
//           </div>

//           <div className="so-panel p-4 mb-5">
//             <div className="flex items-center justify-between mb-3">
//               <div>
//                 <h3 className="so-section-title">
//                   Order Type
//                 </h3>

//                 <p className="so-section-sub">
//                   Select how this customer is ordering
//                 </p>
//               </div>

//               <span className="so-type-tag">
//                 {formatOrderType(orderType)}
//               </span>
//             </div>

//             <div className="grid grid-cols-2 gap-3">
//               <button
//                 type="button"
//                 onClick={() =>
//                   handleOrderTypeChange(
//                     "TAKEAWAY"
//                   )
//                 }
//                 className={`so-type-card ${
//                   orderType === "TAKEAWAY"
//                     ? "so-type-card-active-herb"
//                     : ""
//                 }`}
//               >
//                 <Package
//                   size={25}
//                   color={
//                     orderType === "TAKEAWAY"
//                       ? "#2d4f39"
//                       : "#a49c8d"
//                   }
//                 />

//                 <div className="font-bold so-text-ink mt-2">
//                   Takeaway
//                 </div>

//                 <div className="so-section-sub mt-1">
//                   Customer takes the order
//                 </div>
//               </button>

//               <button
//                 type="button"
//                 onClick={() =>
//                   handleOrderTypeChange(
//                     "DINE_IN"
//                   )
//                 }
//                 className={`so-type-card ${
//                   orderType === "DINE_IN"
//                     ? "so-type-card-active-chili"
//                     : ""
//                 }`}
//               >
//                 <Utensils
//                   size={25}
//                   color={
//                     orderType === "DINE_IN"
//                       ? "#c43f1a"
//                       : "#a49c8d"
//                   }
//                 />

//                 <div className="font-bold so-text-ink mt-2">
//                   Dine In
//                 </div>

//                 <div className="so-section-sub mt-1">
//                   Customer eats at restaurant
//                 </div>
//               </button>
//             </div>
//           </div>

//           {orderType === "DINE_IN" && (
//             <div className="so-panel p-4 mb-5">
//               <div className="flex items-center justify-between mb-3">
//                 <div>
//                   <h3 className="so-section-title">
//                     Select Table
//                   </h3>

//                   <p className="so-section-sub">
//                     Choose an available table
//                   </p>
//                 </div>

//                 {selectedTable && (
//                   <button
//                     type="button"
//                     onClick={() =>
//                       setSelectedTable(null)
//                     }
//                     className="so-clear-link"
//                   >
//                     Clear
//                   </button>
//                 )}
//               </div>

//               {loadingTables ? (
//                 <div className="text-center py-6 so-text-stone">
//                   Loading tables...
//                 </div>
//               ) : tables.length === 0 ? (
//                 <div className="so-empty-note">
//                   No restaurant tables found.
//                 </div>
//               ) : (
//                 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
//                   {tables
//                     .filter(
//                       (table) =>
//                         table.active !== false
//                     )
//                     .map((table) => {
//                       const selected =
//                         selectedTable?.id ===
//                         table.id;

//                       const occupied =
//                         table.status ===
//                         "OCCUPIED";

//                       const unavailable =
//                         table.status ===
//                         "OUT_OF_SERVICE";

//                       return (
//                         <button
//                           key={table.id}
//                           type="button"
//                           disabled={
//                             occupied ||
//                             unavailable
//                           }
//                           onClick={() =>
//                             handleTableSelect(
//                               table
//                             )
//                           }
//                           className={`so-table-card ${
//                             selected
//                               ? "so-table-card-selected"
//                               : occupied
//                               ? "so-table-card-occupied"
//                               : unavailable
//                               ? "so-table-card-unavailable"
//                               : ""
//                           }`}
//                         >
//                           <div className="font-bold so-text-ink">
//                             {table.tableName ||
//                               `Table ${table.id}`}
//                           </div>

//                           <div className="so-section-sub mt-1">
//                             Capacity:{" "}
//                             {table.capacity ||
//                               "-"}
//                           </div>

//                           <div className="text-xs font-semibold mt-2 so-text-stone">
//                             {selected
//                               ? "Selected"
//                               : occupied
//                               ? "Occupied"
//                               : unavailable
//                               ? "Out of Service"
//                               : "Available"}
//                           </div>
//                         </button>
//                       );
//                     })}
//                 </div>
//               )}

//               {orderType === "DINE_IN" &&
//                 !selectedTable && (
//                   <p className="text-xs mt-3" style={{ color: "var(--chili-dark)" }}>
//                     Please select a table.
//                   </p>
//                 )}
//             </div>
//           )}

//           {orderType === "DINE_IN" &&
//             selectedTable && (
//               <div className="so-selected-table-banner mb-5">
//                 <div className="flex items-center justify-between">
//                   <div>
//                     <p className="so-type-tag" style={{ color: "var(--chili-dark)" }}>
//                       SELECTED TABLE
//                     </p>

//                     <p className="text-xl font-bold mt-1" style={{ color: "var(--chili-dark)" }}>
//                       🍽️{" "}
//                       {selectedTable.tableName}
//                     </p>

//                     <p className="text-xs mt-1" style={{ color: "var(--chili-dark)" }}>
//                       Capacity:{" "}
//                       {selectedTable.capacity}
//                     </p>
//                   </div>

//                   <div className="so-dine-badge">
//                     DINE IN
//                   </div>
//                 </div>
//               </div>
//             )}

//           <div className="flex gap-2 mb-5 overflow-x-auto pb-2">
//             <button
//               type="button"
//               onClick={() =>
//                 setSelectedCategory("all")
//               }
//               className={`so-category-pill ${
//                 selectedCategory === "all"
//                   ? "so-category-pill-active"
//                   : ""
//               }`}
//             >
//               All Items
//             </button>

//             {categories.map((category) => (
//               <button
//                 type="button"
//                 key={category.id}
//                 onClick={() =>
//                   setSelectedCategory(
//                     category.id
//                   )
//                 }
//                 className={`so-category-pill ${
//                   String(
//                     selectedCategory
//                   ) === String(category.id)
//                     ? "so-category-pill-active"
//                     : ""
//                 }`}
//               >
//                 {category.name}
//               </button>
//             ))}
//           </div>

//           {loadingMenu ? (
//             <div className="text-center py-12 so-text-stone">
//               Loading menu...
//             </div>
//           ) : filteredItems.length === 0 ? (
//             <div className="text-center py-12 so-text-stone">
//               No menu items available.
//             </div>
//           ) : (
//             <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
//               {filteredItems.map((item) => (
//                 <button
//                   type="button"
//                   key={item.id}
//                   onClick={() =>
//                     addToCart(item)
//                   }
//                   className="so-item-card"
//                 >
//                   <div className="h-28 md:h-32 overflow-hidden flex items-center justify-center">
//                     <img
//                       src={`${API_BASE_URL}/api/menu-items/${item.id}/image`}
//                       alt={item.name}
//                       className="w-full h-full object-cover"
//                       onError={(event) => {
//                         if (item.imageUrl) {
//                           event.currentTarget.src =
//                             item.imageUrl;
//                         } else {
//                           event.currentTarget.style.display =
//                             "none";

//                           const parent =
//                             event.currentTarget
//                               .parentElement;

//                           if (parent) {
//                             parent.innerHTML =
//                               PLATE_SPOON_SVG_MARKUP;
//                           }
//                         }
//                       }}
//                     />
//                   </div>

//                   <div className="p-3">
//                     <h3 className="font-semibold so-text-ink text-sm md:text-base line-clamp-2">
//                       {item.name}
//                     </h3>

//                     <div className="flex justify-between items-center mt-3">
//                       <span className="so-item-price">
//                         {formatCurrency(
//                           item.price
//                         )}
//                       </span>

//                       <span className="so-add-icon">
//                         <Plus size={15} />
//                       </span>
//                     </div>

//                     {gstEnabled && (
//                       <div className="so-item-gst mt-1">
//                         GST{" "}
//                         {getItemTaxRate(item)}%
//                         extra
//                       </div>
//                     )}
//                   </div>
//                 </button>
//               ))}
//             </div>
//           )}
//         </div>

//         <aside className="so-cart-aside hidden md:flex md:w-96 p-5 flex-col">
//           <div className="flex items-center justify-between mb-5">
//             <div className="flex items-center gap-2">
//               <ShoppingCart
//                 size={24}
//                 color="#1c1a17"
//               />

//               <h3 className="so-page-title" style={{ fontSize: "1.5rem" }}>
//                 Direct Bill
//               </h3>
//             </div>

//             <span className="so-badge-direct">
//               DIRECT
//             </span>
//           </div>

//           <div className="so-cart-summary mb-4">
//             <div className="text-xs so-text-stone-light">
//               Order Type
//             </div>

//             <div className="font-bold so-text-ink mt-1">
//               {formatOrderType(orderType)}
//             </div>

//             {orderType === "DINE_IN" &&
//               selectedTable && (
//                 <div className="text-xs so-text-stone mt-1">
//                   Table:{" "}
//                   {selectedTable.tableName}
//                 </div>
//               )}
//           </div>

//           <div className="flex-1 overflow-auto">
//             {renderCart()}
//           </div>
//         </aside>
//       </div>

//       {isMobile &&
//         showCartDrawer && (
//           <div className="fixed inset-0 z-50">
//             <div
//               className="absolute inset-0 so-invoice-overlay"
//               onClick={() =>
//                 setShowCartDrawer(false)
//               }
//             />

//             <div className="so-drawer absolute bottom-0 left-0 right-0 rounded-t-2xl max-h-[90vh] overflow-auto p-4">
//               <div className="flex items-center justify-between mb-4">
//                 <div className="flex items-center gap-2">
//                   <ShoppingCart size={20} color="#1c1a17" />

//                   <h3 className="text-xl font-bold so-text-ink">
//                     Direct Bill
//                   </h3>
//                 </div>

//                 <button
//                   type="button"
//                   onClick={() =>
//                     setShowCartDrawer(false)
//                   }
//                   className="so-drawer-close p-2 rounded-lg"
//                 >
//                   <X size={20} />
//                 </button>
//               </div>

//               {renderCart({ mobile: true })}
//             </div>
//           </div>
//         )}

//       {showInvoice &&
//         invoiceData && (
//           <div className="fixed inset-0 z-[60] so-invoice-overlay flex items-center justify-center px-3">
//             <div className="so-invoice-card shadow-2xl w-full max-w-md max-h-[95vh] overflow-auto p-4 md:p-6">

//               <div
//                 ref={receiptRef}
//                 style={{
//                   background: "white",
//                   paddingBottom: 60,
//                 }}
//               >
//                 <div className="text-center mb-4">
//                   {logoUrl && (
//                     <img
//                       src={logoUrl}
//                       alt="Business Logo"
//                       className="h-14 mx-auto mb-2 object-contain"
//                     />
//                   )}

//                   <h2 className="so-invoice-title">
//                     {businessProfile?.businessName ||
//                       "Restaurant POS"}
//                   </h2>

//                   {businessProfile?.addressLine1 && (
//                     <p className="text-xs so-text-stone mt-1">
//                       {
//                         businessProfile.addressLine1
//                       }
//                     </p>
//                   )}

//                   {(businessProfile?.city ||
//                     businessProfile?.state ||
//                     businessProfile?.pinCode) && (
//                     <p className="text-xs so-text-stone">
//                       {[
//                         businessProfile?.city,
//                         businessProfile?.state,
//                         businessProfile?.pinCode,
//                       ]
//                         .filter(Boolean)
//                         .join(", ")}
//                     </p>
//                   )}

//                   {businessProfile?.phone && (
//                     <p className="text-xs so-text-stone">
//                       Tel:{" "}
//                       {businessProfile.phone}
//                     </p>
//                   )}

//                   {businessProfile?.gstin && (
//                     <p className="text-xs font-semibold so-invoice-gstin mt-1">
//                       GSTIN:{" "}
//                       {businessProfile.gstin}
//                     </p>
//                   )}
//                 </div>

//                 <div className="so-dashed-divider pt-3">
//                   <div className="flex justify-between text-sm so-text-ink">
//                     <span className="font-semibold">
//                       Order
//                     </span>

//                     <span>
//                       {invoiceData.orderNumber}
//                     </span>
//                   </div>

//                   <div className="flex justify-between text-sm mt-1 so-text-ink">
//                     <span className="font-semibold">
//                       Type
//                     </span>

//                     <span>
//                       {formatOrderType(
//                         invoiceData.orderType
//                       )}
//                     </span>
//                   </div>

//                   {invoiceData.orderType ===
//                     "DINE_IN" &&
//                     invoiceData.table && (
//                       <div className="flex justify-between text-sm mt-1 so-text-ink">
//                         <span className="font-semibold">
//                           Table
//                         </span>

//                         <span>
//                           {
//                             invoiceData.table
//                               .tableName
//                           }
//                         </span>
//                       </div>
//                     )}

//                   <div className="flex justify-between text-xs so-text-stone mt-1">
//                     <span>Date</span>

//                     <span>
//                       {formatDateTime(
//                         invoiceData.createdAt
//                       )}
//                     </span>
//                   </div>
//                 </div>

//                 <div className="so-dashed-divider my-3 pt-3">
//                   {invoiceData.items.map(
//                     (item) => (
//                       <div
//                         key={item.id}
//                         className="mb-3"
//                       >
//                         <div className="flex justify-between text-sm so-text-ink">
//                           <div>
//                             <span>
//                               {item.name}
//                             </span>

//                             <span className="text-xs so-text-stone ml-2">
//                               ×{" "}
//                               {item.quantity}
//                             </span>
//                           </div>

//                           <span className="font-medium">
//                             {formatCurrency(
//                               item.subtotal
//                             )}
//                           </span>
//                         </div>

//                         {gstEnabled &&
//                           item.taxRate > 0 && (
//                             <div className="flex justify-between text-xs so-text-stone mt-1">
//                               <span>
//                                 GST{" "}
//                                 {item.taxRate}%
//                               </span>

//                               <span>
//                                 +
//                                 {formatCurrency(
//                                   item.taxAmount
//                                 )}
//                               </span>
//                             </div>
//                           )}

//                         <div className="flex justify-between text-xs font-semibold mt-1 so-text-ink">
//                           <span>
//                             Item Total
//                           </span>

//                           <span>
//                             {formatCurrency(
//                               item.lineTotal
//                             )}
//                           </span>
//                         </div>
//                       </div>
//                     )
//                   )}
//                 </div>

//                 <div className="so-dashed-divider pt-3">
//                   <div className="flex justify-between text-sm so-text-ink">
//                     <span>
//                       Sub-total
//                     </span>

//                     <span>
//                       {formatCurrency(
//                         invoiceData.subtotal
//                       )}
//                     </span>
//                   </div>

//                   {invoiceData.gstEnabled &&
//                     invoiceData.taxAmount > 0 && (
//                       <div className="flex justify-between text-sm mt-2 so-text-ink">
//                         <span>
//                           GST
//                         </span>

//                         <span>
//                           {formatCurrency(
//                             invoiceData.taxAmount
//                           )}
//                         </span>
//                       </div>
//                     )}

//                   <div className="flex justify-between text-lg font-bold mt-3 so-text-ink">
//                     <span>TOTAL</span>

//                     <span className="so-invoice-total">
//                       {formatCurrency(
//                         invoiceData.totalAmount
//                       )}
//                     </span>
//                   </div>
//                 </div>

//                 {invoiceData.specialInstructions && (
//                   <div className="so-dashed-divider mt-3 pt-3">
//                     <p className="text-xs so-text-ink">
//                       <span className="font-semibold">
//                         Notes:
//                       </span>{" "}
//                       {
//                         invoiceData.specialInstructions
//                       }
//                     </p>
//                   </div>
//                 )}

//                 <p className="text-center text-sm so-text-stone mt-5">
//                   Thank you for your purchase!
//                 </p>
//               </div>

//               <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
//                 <button
//                   type="button"
//                   onClick={() =>
//                     setShowInvoice(false)
//                   }
//                   disabled={isProcessing}
//                   className="so-invoice-btn-outline rounded-lg py-3 text-sm font-medium"
//                 >
//                   Back
//                 </button>

//                 <button
//                   type="button"
//                   onClick={handlePrint}
//                   disabled={isProcessing}
//                   className="so-invoice-btn-print rounded-lg py-3 text-sm font-semibold"
//                 >
//                   🖨 Print
//                 </button>

//                 <button
//                   type="button"
//                   onClick={handleDownloadPDF}
//                   disabled={isProcessing}
//                   className="so-invoice-btn-pdf rounded-lg py-3 text-sm font-semibold"
//                 >
//                   PDF
//                 </button>
//               </div>

//               <button
//                 type="button"
//                 onClick={handleInvoiceDone}
//                 disabled={isProcessing}
//                 className="so-invoice-btn-done w-full mt-3 py-3 rounded-lg font-bold"
//               >
//                 {isProcessing
//                   ? "Saving Order..."
//                   : "✓ Done & Save Order"}
//               </button>

//               <p className="text-center text-xs so-text-stone mt-2">
//                 This order will be saved directly
//                 to Order History.
//               </p>
//             </div>
//           </div>
//         )}
//     </>
//   );
// }









// // // src/components/DirectPOSView.js

// // import React, { useEffect, useRef, useState } from "react";
// // import {
// //   ShoppingCart,
// //   Plus,
// //   Minus,
// //   Trash2,
// //   Utensils,
// //   Package,
// //   X,
// // } from "lucide-react";

// // import {
// //   getAvailableMenuItems,
// //   getCategories,
// //   getTables,
// //   createDirectOrder,
// //   getBusinessProfile,
// //   getBusinessLogoBlob,
// // } from "../api";

// // import jsPDF from "jspdf";
// // import html2canvas from "html2canvas";

// // const API_BASE_URL =
// //   process.env.REACT_APP_API_URL || "http://localhost:8080";

// // // =========================================================
// // // DEFAULT "NO IMAGE" ILLUSTRATION — plate & spoon, SwiftOrder style
// // // Used both as a React fallback element and, as a raw string,
// // // inside the onError innerHTML swap (img tags can't onError-recover
// // // to JSX, only to markup).
// // // =========================================================

// // const PLATE_SPOON_SVG_MARKUP = `
// //   <div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#f2ede1;">
// //     <svg width="54" height="54" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
// //       <circle cx="32" cy="32" r="27" fill="#fbf9f4" stroke="#ddd4c2" stroke-width="2"/>
// //       <circle cx="32" cy="32" r="17" fill="none" stroke="#ddd4c2" stroke-width="2"/>
// //       <path d="M17 16 L17 30 C17 32.2 18.8 34 21 34 C23.2 34 25 32.2 25 30 L25 16" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
// //       <line x1="21" y1="16" x2="21" y2="48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round"/>
// //       <path d="M45 16 C41 16 39 20 39 25 C39 29 41 31 45 31 L45 48" stroke="#a49c8d" stroke-width="2" stroke-linecap="round" fill="none"/>
// //     </svg>
// //   </div>
// // `;

// // function PlateSpoonPlaceholder() {
// //   return (
// //     <div className="so-no-image">
// //       <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
// //         <circle cx="32" cy="32" r="27" fill="#fbf9f4" stroke="#ddd4c2" strokeWidth="2" />
// //         <circle cx="32" cy="32" r="17" fill="none" stroke="#ddd4c2" strokeWidth="2" />
// //         <path d="M17 16 L17 30 C17 32.2 18.8 34 21 34 C23.2 34 25 32.2 25 30 L25 16" stroke="#a49c8d" strokeWidth="2" strokeLinecap="round" />
// //         <line x1="21" y1="16" x2="21" y2="48" stroke="#a49c8d" strokeWidth="2" strokeLinecap="round" />
// //         <path d="M45 16 C41 16 39 20 39 25 C39 29 41 31 45 31 L45 48" stroke="#a49c8d" strokeWidth="2" strokeLinecap="round" fill="none" />
// //       </svg>
// //     </div>
// //   );
// // }

// // export default function DirectPOSView() {
// //   const [menuItems, setMenuItems] = useState([]);
// //   const [categories, setCategories] = useState([]);
// //   const [selectedCategory, setSelectedCategory] = useState("all");

// //   const [cart, setCart] = useState([]);

// //   const [orderType, setOrderType] = useState("TAKEAWAY");
// //   const [tables, setTables] = useState([]);
// //   const [selectedTable, setSelectedTable] = useState(null);

// //   const [specialInstructions, setSpecialInstructions] = useState("");

// //   const [businessProfile, setBusinessProfile] = useState(null);
// //   const [logoUrl, setLogoUrl] = useState(null);

// //   const [showInvoice, setShowInvoice] = useState(false);
// //   const [invoiceData, setInvoiceData] = useState(null);

// //   const [isProcessing, setIsProcessing] = useState(false);
// //   const [loadingMenu, setLoadingMenu] = useState(true);
// //   const [loadingTables, setLoadingTables] = useState(true);

// //   const [showCartDrawer, setShowCartDrawer] = useState(false);
// //   const [isMobile, setIsMobile] = useState(
// //     typeof window !== "undefined" ? window.innerWidth < 768 : false
// //   );

// //   const receiptRef = useRef(null);

// //   useEffect(() => {
// //     const handleResize = () => {
// //       setIsMobile(window.innerWidth < 768);
// //     };

// //     window.addEventListener("resize", handleResize);

// //     return () => {
// //       window.removeEventListener("resize", handleResize);
// //     };
// //   }, []);

// //   useEffect(() => {
// //     loadMenu();
// //     loadCategories();
// //     loadTables();
// //     loadBusinessProfile();
// //   }, []);

// //   const loadMenu = async () => {
// //     try {
// //       setLoadingMenu(true);

// //       const data = await getAvailableMenuItems();

// //       setMenuItems(Array.isArray(data) ? data : []);
// //     } catch (error) {
// //       console.error("Failed to load menu:", error);
// //       alert("Unable to load menu items.");
// //     } finally {
// //       setLoadingMenu(false);
// //     }
// //   };

// //   const loadCategories = async () => {
// //     try {
// //       const data = await getCategories();

// //       setCategories(Array.isArray(data) ? data : []);
// //     } catch (error) {
// //       console.error("Failed to load categories:", error);
// //     }
// //   };

// //   const loadTables = async () => {
// //     try {
// //       setLoadingTables(true);

// //       const data = await getTables();

// //       setTables(Array.isArray(data) ? data : []);
// //     } catch (error) {
// //       console.error("Failed to load tables:", error);
// //       setTables([]);
// //     } finally {
// //       setLoadingTables(false);
// //     }
// //   };

// //   const loadBusinessProfile = async () => {
// //     try {
// //       const data = await getBusinessProfile();

// //       if (!data) return;

// //       setBusinessProfile(data);

// //       if (data.hasLogo) {
// //         try {
// //           const response = await getBusinessLogoBlob();

// //           const url = URL.createObjectURL(response.data);

// //           setLogoUrl(url);
// //         } catch (error) {
// //           console.error("Failed to load business logo:", error);
// //         }
// //       }
// //     } catch (error) {
// //       console.error("Failed to load business profile:", error);
// //     }
// //   };

// //   useEffect(() => {
// //     return () => {
// //       if (logoUrl) {
// //         URL.revokeObjectURL(logoUrl);
// //       }
// //     };
// //   }, [logoUrl]);

// //   const toNumber = (value) => {
// //     const number = Number(value);

// //     return Number.isFinite(number) ? number : 0;
// //   };

// //   const roundMoney = (value) => {
// //     return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
// //   };

// //   const gstEnabled = Boolean(businessProfile?.gstEnabled);

// //   const businessDefaultTaxRate = gstEnabled
// //     ? toNumber(businessProfile?.defaultTaxRate)
// //     : 0;

// //   const getItemTaxRate = (menuItem) => {
// //     const itemRate = toNumber(menuItem?.taxRate);

// //     if (itemRate > 0) {
// //       return itemRate;
// //     }

// //     return businessDefaultTaxRate;
// //   };

// //   /*
// //    * DIRECT POS GST IS ALWAYS EXTRA.
// //    *
// //    * ₹500 + 5% GST = ₹525
// //    * ₹500 + 3% GST = ₹515
// //    */
// //   const calculateLine = (menuItem, quantity) => {
// //     const price = roundMoney(menuItem?.price);

// //     const qty = Math.max(
// //       0,
// //       Math.floor(toNumber(quantity))
// //     );

// //     const subtotal = roundMoney(price * qty);

// //     if (!gstEnabled) {
// //       return {
// //         subtotal,
// //         taxRate: 0,
// //         taxAmount: 0,
// //         total: subtotal,
// //       };
// //     }

// //     const taxRate = roundMoney(
// //       getItemTaxRate(menuItem)
// //     );

// //     if (taxRate <= 0) {
// //       return {
// //         subtotal,
// //         taxRate: 0,
// //         taxAmount: 0,
// //         total: subtotal,
// //       };
// //     }

// //     const taxAmount = roundMoney(
// //       subtotal * taxRate / 100
// //     );

// //     const total = roundMoney(
// //       subtotal + taxAmount
// //     );

// //     return {
// //       subtotal,
// //       taxRate,
// //       taxAmount,
// //       total,
// //     };
// //   };

// //   const calculateCartTotals = () => {
// //     let subtotal = 0;
// //     let taxAmount = 0;
// //     let totalAmount = 0;

// //     cart.forEach((item) => {
// //       const line = calculateLine(
// //         item.menuItem,
// //         item.quantity
// //       );

// //       subtotal = roundMoney(
// //         subtotal + line.subtotal
// //       );

// //       taxAmount = roundMoney(
// //         taxAmount + line.taxAmount
// //       );

// //       totalAmount = roundMoney(
// //         totalAmount + line.total
// //       );
// //     });

// //     return {
// //       subtotal,
// //       taxAmount,
// //       totalAmount,
// //     };
// //   };

// //   const filteredItems =
// //     selectedCategory === "all"
// //       ? menuItems
// //       : menuItems.filter(
// //           (item) =>
// //             item.category &&
// //             String(item.category.id) ===
// //               String(selectedCategory)
// //         );

// //   const addToCart = (menuItem) => {
// //     setCart((previousCart) => {
// //       const existing = previousCart.find(
// //         (item) =>
// //           String(item.menuItem.id) ===
// //           String(menuItem.id)
// //       );

// //       if (existing) {
// //         return previousCart.map((item) =>
// //           String(item.menuItem.id) ===
// //           String(menuItem.id)
// //             ? {
// //                 ...item,
// //                 quantity: item.quantity + 1,
// //               }
// //             : item
// //         );
// //       }

// //       return [
// //         ...previousCart,
// //         {
// //           menuItem,
// //           quantity: 1,
// //         },
// //       ];
// //     });

// //     if (isMobile) {
// //       setShowCartDrawer(true);
// //     }
// //   };

// //   const updateQuantity = (menuItemId, change) => {
// //     setCart((previousCart) =>
// //       previousCart
// //         .map((item) =>
// //           String(item.menuItem.id) ===
// //           String(menuItemId)
// //             ? {
// //                 ...item,
// //                 quantity:
// //                   item.quantity + change,
// //               }
// //             : item
// //         )
// //         .filter((item) => item.quantity > 0)
// //     );
// //   };

// //   const removeFromCart = (menuItemId) => {
// //     setCart((previousCart) =>
// //       previousCart.filter(
// //         (item) =>
// //           String(item.menuItem.id) !==
// //           String(menuItemId)
// //       )
// //     );
// //   };

// //   const getTotalItems = () => {
// //     return cart.reduce(
// //       (total, item) =>
// //         total + item.quantity,
// //       0
// //     );
// //   };

// //   const handleOrderTypeChange = (type) => {
// //     setOrderType(type);

// //     if (type === "TAKEAWAY") {
// //       setSelectedTable(null);
// //     }
// //   };

// //   const handleTableSelect = (table) => {
// //     if (!table) return;

// //     if (table.status === "OCCUPIED") {
// //       alert(
// //         `${table.tableName} is currently occupied.`
// //       );
// //       return;
// //     }

// //     if (table.status === "OUT_OF_SERVICE") {
// //       alert(
// //         `${table.tableName} is out of service.`
// //       );
// //       return;
// //     }

// //     setSelectedTable(table);
// //   };

// //   const generateOrderNumber = () => {
// //     return `POS-${Date.now()}`;
// //   };

// //   const formatCurrency = (value) => {
// //     return new Intl.NumberFormat("en-IN", {
// //       style: "currency",
// //       currency: "INR",
// //       minimumFractionDigits: 2,
// //       maximumFractionDigits: 2,
// //     }).format(toNumber(value));
// //   };

// //   const formatDateTime = (value) => {
// //     if (!value) return "";

// //     return new Date(value).toLocaleString("en-IN");
// //   };

// //   const formatOrderType = (type) => {
// //     return type === "DINE_IN"
// //       ? "🍽️ DINE IN"
// //       : "🥡 TAKEAWAY";
// //   };

// //   const handleGenerateBill = () => {
// //     if (cart.length === 0) {
// //       alert("Please add at least one item.");
// //       return;
// //     }

// //     if (
// //       orderType === "DINE_IN" &&
// //       !selectedTable
// //     ) {
// //       alert("Please select a table for Dine In.");
// //       return;
// //     }

// //     const orderNumber = generateOrderNumber();

// //     const totals = calculateCartTotals();

// //     const invoiceItems = cart.map((item) => {
// //       const line = calculateLine(
// //         item.menuItem,
// //         item.quantity
// //       );

// //       return {
// //         id: item.menuItem.id,
// //         name: item.menuItem.name,
// //         price: roundMoney(item.menuItem.price),
// //         quantity: item.quantity,
// //         taxRate: line.taxRate,
// //         taxAmount: line.taxAmount,
// //         subtotal: line.subtotal,
// //         lineTotal: line.total,
// //       };
// //     });

// //     setInvoiceData({
// //       orderNumber,
// //       orderType,
// //       table: selectedTable,
// //       specialInstructions,
// //       createdAt: new Date().toISOString(),

// //       gstEnabled,

// //       taxInclusive: false,

// //       defaultTaxRate:
// //         businessDefaultTaxRate,

// //       subtotal: totals.subtotal,
// //       taxAmount: totals.taxAmount,
// //       totalAmount: totals.totalAmount,

// //       items: invoiceItems,
// //     });

// //     setShowInvoice(true);
// //   };

// //   const handleInvoiceDone = async () => {
// //     if (!invoiceData) return;

// //     setIsProcessing(true);

// //     try {
// //       const payload = {
// //         orderNumber:
// //           invoiceData.orderNumber,

// //         orderType:
// //           invoiceData.orderType,

// //         tableId:
// //           invoiceData.orderType === "DINE_IN"
// //             ? invoiceData.table?.id
// //             : null,

// //         specialInstructions:
// //           invoiceData.specialInstructions || null,

// //         items: invoiceData.items.map(
// //           (item) => ({
// //             menuItemId: item.id,
// //             quantity: item.quantity,

// //             /*
// //              * IMPORTANT:
// //              * Send ORIGINAL menu price.
// //              *
// //              * ₹500 is sent.
// //              * Backend calculates:
// //              * GST ₹25
// //              * Total ₹525
// //              */
// //             price: item.price,

// //             specialInstructions: null,

// //             taxRate: item.taxRate,
// //           })
// //         ),
// //       };

// //       const savedOrder =
// //         await createDirectOrder(payload);

// //       console.log(
// //         "DIRECT ORDER SAVED:",
// //         savedOrder
// //       );

// //       setCart([]);
// //       setOrderType("TAKEAWAY");
// //       setSelectedTable(null);
// //       setSpecialInstructions("");
// //       setInvoiceData(null);
// //       setShowInvoice(false);
// //       setShowCartDrawer(false);

// //       await loadTables();

// //       alert("Order saved successfully.");
// //     } catch (error) {
// //       console.error(
// //         "Direct POS order failed:",
// //         error
// //       );

// //       console.error(
// //         "Server response:",
// //         error?.response?.data
// //       );

// //       const serverMessage =
// //         error?.response?.data;

// //       alert(
// //         typeof serverMessage === "string"
// //           ? serverMessage
// //           : serverMessage?.message ||
// //             "Unable to save order."
// //       );
// //     } finally {
// //       setIsProcessing(false);
// //     }
// //   };

// //   const handlePrint = () => {
// //     if (!receiptRef.current) return;

// //     const content =
// //       receiptRef.current.innerHTML;

// //     const printWindow = window.open(
// //       "",
// //       "_blank",
// //       "width=420,height=750"
// //     );

// //     if (!printWindow) {
// //       alert(
// //         "Popup blocked. Please allow popups to print."
// //       );
// //       return;
// //     }

// //     printWindow.document.open();

// //     printWindow.document.write(`
// //       <html>
// //         <head>
// //           <title>Invoice</title>
// //           <meta
// //             name="viewport"
// //             content="width=device-width, initial-scale=1"
// //           />

// //           <style>
// //             * {
// //               box-sizing: border-box;
// //             }

// //             body {
// //               font-family: Arial, Helvetica, sans-serif;
// //               margin: 0;
// //               padding: 20px;
// //               background: white;
// //               color: #111827;
// //             }

// //             .row {
// //               display: flex;
// //               justify-content: space-between;
// //               gap: 10px;
// //             }

// //             img {
// //               max-width: 120px;
// //               max-height: 70px;
// //               object-fit: contain;
// //             }
// //           </style>
// //         </head>

// //         <body>
// //           ${content}
// //         </body>
// //       </html>
// //     `);

// //     printWindow.document.close();
// //     printWindow.focus();

// //     setTimeout(() => {
// //       printWindow.print();
// //       printWindow.close();
// //     }, 400);
// //   };

// //   const handleDownloadPDF = async () => {
// //     if (
// //       !receiptRef.current ||
// //       !invoiceData
// //     ) {
// //       return;
// //     }

// //     const element = receiptRef.current;

// //     const originalWidth =
// //       element.style.width;

// //     const originalMaxHeight =
// //       element.style.maxHeight;

// //     const originalPaddingBottom =
// //       element.style.paddingBottom;

// //     try {
// //       const targetWidth =
// //         window.innerWidth > 900
// //           ? 700
// //           : Math.floor(
// //               window.innerWidth * 0.92
// //             );

// //       element.style.width =
// //         `${targetWidth}px`;

// //       element.style.maxHeight = "none";
// //       element.style.paddingBottom = "60px";

// //       const canvas =
// //         await html2canvas(
// //           element,
// //           {
// //             scale:
// //               Math.min(
// //                 2,
// //                 window.devicePixelRatio ||
// //                   1.5
// //               ) * 1.2,

// //             useCORS: true,

// //             scrollX: 0,
// //             scrollY: -window.scrollY,

// //             windowWidth:
// //               element.scrollWidth,

// //             windowHeight:
// //               element.scrollHeight,
// //           }
// //         );

// //       const imageData =
// //         canvas.toDataURL("image/png");

// //       const pdf =
// //         new jsPDF(
// //           "p",
// //           "pt",
// //           "a4"
// //         );

// //       const pageWidth =
// //         pdf.internal.pageSize.getWidth();

// //       const pageHeight =
// //         pdf.internal.pageSize.getHeight();

// //       const margin = 20;

// //       const imageWidth =
// //         pageWidth - margin * 2;

// //       const imageHeight =
// //         (canvas.height *
// //           imageWidth) /
// //         canvas.width;

// //       let heightLeft =
// //         imageHeight;

// //       let position = margin;

// //       pdf.addImage(
// //         imageData,
// //         "PNG",
// //         margin,
// //         position,
// //         imageWidth,
// //         imageHeight
// //       );

// //       heightLeft -=
// //         pageHeight - margin * 2;

// //       while (heightLeft > 0) {
// //         pdf.addPage();

// //         position =
// //           margin -
// //           (
// //             imageHeight -
// //             heightLeft
// //           );

// //         pdf.addImage(
// //           imageData,
// //           "PNG",
// //           margin,
// //           position,
// //           imageWidth,
// //           imageHeight
// //         );

// //         heightLeft -=
// //           pageHeight - margin * 2;
// //       }

// //       pdf.save(
// //         `invoice_${invoiceData.orderNumber}.pdf`
// //       );
// //     } catch (error) {
// //       console.error(
// //         "PDF generation failed:",
// //         error
// //       );

// //       alert(
// //         "Failed to generate PDF."
// //       );
// //     } finally {
// //       element.style.width =
// //         originalWidth || "";

// //       element.style.maxHeight =
// //         originalMaxHeight || "";

// //       element.style.paddingBottom =
// //         originalPaddingBottom || "";
// //     }
// //   };

// //   const CartContent = ({ mobile = false } = {}) => {
// //     const totals =
// //       calculateCartTotals();

// //     return (
// //       <>
// //         {cart.length === 0 ? (
// //           <div className="text-center py-10 so-text-stone-light">
// //             <ShoppingCart
// //               size={48}
// //               className="mx-auto mb-3 opacity-40"
// //             />

// //             <p>Cart is empty</p>

// //             <p className="text-xs mt-1">
// //               Add menu items to continue.
// //             </p>
// //           </div>
// //         ) : (
// //           <div className="space-y-3">
// //             {cart.map((item) => {
// //               const line =
// //                 calculateLine(
// //                   item.menuItem,
// //                   item.quantity
// //                 );

// //               return (
// //                 <div
// //                   key={item.menuItem.id}
// //                   className="so-cart-line rounded-lg p-3"
// //                 >
// //                   <div className="flex justify-between items-start">
// //                     <div className="pr-2">
// //                       <h4 className="font-semibold so-text-ink">
// //                         {item.menuItem.name}
// //                       </h4>

// //                       <p className="text-xs so-text-stone mt-1">
// //                         {formatCurrency(
// //                           item.menuItem.price
// //                         )}{" "}
// //                         each
// //                       </p>

// //                       {gstEnabled &&
// //                         line.taxRate > 0 && (
// //                           <p className="text-xs so-text-stone mt-1">
// //                             GST{" "}
// //                             {line.taxRate}%
// //                             {" + "}
// //                             {formatCurrency(
// //                               line.taxAmount
// //                             )}
// //                           </p>
// //                         )}
// //                     </div>

// //                     <button
// //                       type="button"
// //                       onClick={() =>
// //                         removeFromCart(
// //                           item.menuItem.id
// //                         )
// //                       }
// //                       className="so-remove-btn"
// //                     >
// //                       <Trash2 size={16} />
// //                     </button>
// //                   </div>

// //                   <div className="flex justify-between items-center mt-3">
// //                     <div className="flex items-center gap-2">
// //                       <button
// //                         type="button"
// //                         onClick={() =>
// //                           updateQuantity(
// //                             item.menuItem.id,
// //                             -1
// //                           )
// //                         }
// //                         className="so-qty-btn"
// //                       >
// //                         <Minus size={15} />
// //                       </button>

// //                       <span className="w-8 text-center font-semibold so-text-ink">
// //                         {item.quantity}
// //                       </span>

// //                       <button
// //                         type="button"
// //                         onClick={() =>
// //                           updateQuantity(
// //                             item.menuItem.id,
// //                             1
// //                           )
// //                         }
// //                         className="so-qty-btn"
// //                       >
// //                         <Plus size={15} />
// //                       </button>
// //                     </div>

// //                     <div className="text-right">
// //                       <div className="font-bold so-text-ink">
// //                         {formatCurrency(
// //                           line.total
// //                         )}
// //                       </div>

// //                       {gstEnabled &&
// //                         line.taxAmount > 0 && (
// //                           <div className="text-xs so-text-stone">
// //                             GST{" "}
// //                             {formatCurrency(
// //                               line.taxAmount
// //                             )}
// //                           </div>
// //                         )}
// //                     </div>
// //                   </div>
// //                 </div>
// //               );
// //             })}
// //           </div>
// //         )}

// //         <div className="mt-4">
// //           <label className="block text-sm font-semibold so-text-ink mb-1">
// //             Order Notes
// //           </label>

// //           <textarea
// //             value={specialInstructions}
// //             onChange={(e) =>
// //               setSpecialInstructions(
// //                 e.target.value
// //               )
// //             }
// //             rows={3}
// //             maxLength={500}
// //             placeholder="e.g. Less spicy, extra sauce, no onion..."
// //             className="so-textarea w-full rounded-lg p-3 text-sm"
// //           />

// //           <div className="text-right text-xs so-text-stone-light mt-1">
// //             {specialInstructions.length}/500
// //           </div>
// //         </div>

// //         <div className="so-dashed-divider mt-4 pt-4">
// //           <div className="flex justify-between text-sm mb-2">
// //             <span className="so-text-stone">
// //               {getTotalItems()} item
// //               {getTotalItems() !== 1
// //                 ? "s"
// //                 : ""}
// //             </span>
// //           </div>

// //           <div className="flex justify-between text-sm so-text-ink">
// //             <span>Sub-total</span>

// //             <span>
// //               {formatCurrency(
// //                 totals.subtotal
// //               )}
// //             </span>
// //           </div>

// //           {gstEnabled &&
// //             totals.taxAmount > 0 && (
// //               <div className="flex justify-between text-sm mt-2 so-text-ink">
// //                 <span>
// //                   GST
// //                   {businessDefaultTaxRate > 0
// //                     ? ` (${businessDefaultTaxRate}%)`
// //                     : ""}
// //                 </span>

// //                 <span>
// //                   {formatCurrency(
// //                     totals.taxAmount
// //                   )}
// //                 </span>
// //               </div>
// //             )}

// //           <div className="flex justify-between items-center mt-3">
// //             <span className="text-xl font-bold so-text-ink">
// //               Total
// //             </span>

// //             <span className="text-2xl font-bold so-text-herb">
// //               {formatCurrency(
// //                 totals.totalAmount
// //               )}
// //             </span>
// //           </div>

// //           <button
// //             type="button"
// //             onClick={() => {
// //               handleGenerateBill();

// //               if (mobile) {
// //                 setShowCartDrawer(false);
// //               }
// //             }}
// //             disabled={
// //               cart.length === 0 ||
// //               isProcessing
// //             }
// //             className="so-btn-primary w-full mt-4 py-3 rounded-lg font-semibold"
// //           >
// //             🧾 Generate Bill
// //           </button>
// //         </div>
// //       </>
// //     );
// //   };

// //   return (
// //     <>
// //       {/* Scoped SwiftOrder theme — fonts, tokens and ticket styling.
// //           Same tokens as Login.js / Dashboard.js / Sidebar.js. */}
// //       <style>{`
// //         @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

// //         .so-dpos {
// //           --ink: #1c1a17;
// //           --paper: #fbf9f4;
// //           --paper-dim: #f2ede1;
// //           --chili: #e4572e;
// //           --chili-dark: #c43f1a;
// //           --herb: #3f6b4f;
// //           --herb-dark: #2d4f39;
// //           --mustard: #f2a93b;
// //           --stone: #6b6459;
// //           --stone-light: #a49c8d;
// //           --line: #ddd4c2;
// //           --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
// //           --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
// //           --font-mono: 'JetBrains Mono', 'Courier New', monospace;

// //           background: var(--paper-dim);
// //           font-family: var(--font-body);
// //           color: var(--ink);
// //         }

// //         .so-dpos * {
// //           box-sizing: border-box;
// //         }

// //         .so-text-ink { color: var(--ink); }
// //         .so-text-stone { color: var(--stone); }
// //         .so-text-stone-light { color: var(--stone-light); }
// //         .so-text-herb { color: var(--herb-dark); }

// //         .so-page-title {
// //           font-family: var(--font-display);
// //           font-size: 1.9rem;
// //           letter-spacing: 0.01em;
// //           color: var(--ink);
// //         }

// //         .so-badge-direct {
// //           background: var(--mustard);
// //           color: #633806;
// //           font-family: var(--font-mono);
// //           font-size: 0.68rem;
// //           font-weight: 700;
// //           letter-spacing: 0.06em;
// //           padding: 4px 10px;
// //           border-radius: 999px;
// //         }

// //         .so-subtitle {
// //           font-size: 0.85rem;
// //           color: var(--stone);
// //         }

// //         .so-gst-note {
// //           font-size: 0.72rem;
// //           color: var(--herb-dark);
// //           font-weight: 700;
// //           font-family: var(--font-mono);
// //           letter-spacing: 0.02em;
// //         }

// //         .so-cart-toggle {
// //           display: flex;
// //           align-items: center;
// //           gap: 8px;
// //           background: var(--chili);
// //           color: var(--paper);
// //           padding: 10px 14px;
// //           border-radius: 8px;
// //           font-weight: 700;
// //           font-size: 0.88rem;
// //           box-shadow: 0 8px 18px -8px rgba(228, 87, 46, 0.55);
// //         }

// //         .so-panel {
// //           background: #fffdf8;
// //           border: 1px solid var(--line);
// //           border-radius: 12px;
// //           box-shadow: 0 10px 26px -20px rgba(28, 26, 23, 0.3);
// //         }

// //         .so-section-title {
// //           font-weight: 700;
// //           color: var(--ink);
// //           font-size: 0.98rem;
// //         }

// //         .so-section-sub {
// //           font-size: 0.74rem;
// //           color: var(--stone-light);
// //         }

// //         .so-type-tag {
// //           font-family: var(--font-mono);
// //           font-size: 0.7rem;
// //           font-weight: 700;
// //           color: var(--stone);
// //         }

// //         .so-type-card {
// //           border-radius: 10px;
// //           border: 1.5px solid var(--line);
// //           padding: 16px;
// //           text-align: left;
// //           background: var(--paper);
// //           transition: border-color 0.15s ease, background 0.15s ease;
// //         }

// //         .so-type-card:hover {
// //           border-color: var(--stone-light);
// //         }

// //         .so-type-card-active-herb {
// //           border-color: var(--herb);
// //           background: #eaf3ee;
// //         }

// //         .so-type-card-active-chili {
// //           border-color: var(--chili);
// //           background: #fdece6;
// //         }

// //         .so-clear-link {
// //           font-size: 0.74rem;
// //           color: var(--chili);
// //           font-weight: 600;
// //         }

// //         .so-clear-link:hover {
// //           color: var(--chili-dark);
// //         }

// //         .so-empty-note {
// //           background: #fdf3e2;
// //           border: 1px solid var(--mustard);
// //           border-radius: 8px;
// //           padding: 10px 14px;
// //           font-size: 0.85rem;
// //           color: #854f0b;
// //         }

// //         .so-table-card {
// //           border-radius: 10px;
// //           border: 1.5px solid var(--line);
// //           padding: 12px;
// //           text-align: center;
// //           background: var(--paper);
// //           transition: border-color 0.15s ease, background 0.15s ease;
// //         }

// //         .so-table-card-selected {
// //           border-color: var(--chili);
// //           background: #fdece6;
// //         }

// //         .so-table-card-occupied {
// //           border-color: #f0ccc4;
// //           background: #fdf4f1;
// //           cursor: not-allowed;
// //         }

// //         .so-table-card-unavailable {
// //           border-color: var(--line);
// //           background: var(--paper-dim);
// //           cursor: not-allowed;
// //         }

// //         .so-table-card:hover:not(:disabled) {
// //           border-color: var(--chili);
// //         }

// //         .so-selected-table-banner {
// //           background: #fdece6;
// //           border: 1px solid var(--chili);
// //           border-radius: 12px;
// //           padding: 16px;
// //         }

// //         .so-dine-badge {
// //           background: var(--chili);
// //           color: var(--paper);
// //           padding: 8px 12px;
// //           border-radius: 8px;
// //           font-size: 0.85rem;
// //           font-weight: 700;
// //           font-family: var(--font-mono);
// //         }

// //         .so-category-pill {
// //           padding: 8px 16px;
// //           border-radius: 999px;
// //           white-space: nowrap;
// //           font-size: 0.85rem;
// //           font-weight: 600;
// //           background: #fff;
// //           color: var(--stone);
// //           border: 1px solid var(--line);
// //           transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
// //         }

// //         .so-category-pill:hover {
// //           border-color: var(--chili);
// //         }

// //         .so-category-pill-active {
// //           background: var(--ink);
// //           color: var(--paper);
// //           border-color: var(--ink);
// //         }

// //         .so-item-card {
// //           background: #fffdf8;
// //           border-radius: 12px;
// //           border: 1px solid var(--line);
// //           overflow: hidden;
// //           text-align: left;
// //           transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
// //         }

// //         .so-item-card:hover {
// //           border-color: var(--chili);
// //           transform: translateY(-2px);
// //           box-shadow: 0 14px 24px -18px rgba(28, 26, 23, 0.4);
// //         }

// //         .so-no-image {
// //           width: 100%;
// //           height: 100%;
// //           display: flex;
// //           align-items: center;
// //           justify-content: center;
// //           background: var(--paper-dim);
// //         }

// //         .so-item-price {
// //           font-weight: 700;
// //           color: var(--herb-dark);
// //         }

// //         .so-add-icon {
// //           background: var(--chili);
// //           color: var(--paper);
// //           border-radius: 999px;
// //           padding: 6px;
// //           display: inline-flex;
// //         }

// //         .so-item-gst {
// //           font-size: 0.7rem;
// //           color: var(--stone-light);
// //         }

// //         .so-cart-aside {
// //           background: #fffdf8;
// //           box-shadow: -8px 0 24px -20px rgba(28, 26, 23, 0.4);
// //           border-left: 1px solid var(--line);
// //         }

// //         .so-cart-summary {
// //           background: var(--paper-dim);
// //           border-radius: 10px;
// //           padding: 12px;
// //         }

// //         .so-cart-line {
// //           background: var(--paper-dim);
// //         }

// //         .so-remove-btn {
// //           color: var(--chili);
// //         }

// //         .so-remove-btn:hover {
// //           color: var(--chili-dark);
// //         }

// //         .so-qty-btn {
// //           width: 32px;
// //           height: 32px;
// //           background: #fff;
// //           border-radius: 6px;
// //           display: flex;
// //           align-items: center;
// //           justify-content: center;
// //           border: 1px solid var(--line);
// //           color: var(--ink);
// //         }

// //         .so-qty-btn:hover {
// //           background: var(--paper-dim);
// //         }

// //         .so-textarea {
// //           border: 1px solid var(--line);
// //           background: var(--paper);
// //           color: var(--ink);
// //           outline: none;
// //           transition: border-color 0.15s ease, box-shadow 0.15s ease;
// //         }

// //         .so-textarea:focus {
// //           border-color: var(--chili);
// //           box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
// //         }

// //         .so-dashed-divider {
// //           border-top: 1px dashed var(--line);
// //         }

// //         .so-btn-primary {
// //           background: var(--chili);
// //           color: var(--paper);
// //           box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
// //           transition: background 0.15s ease, transform 0.1s ease, opacity 0.15s ease;
// //         }

// //         .so-btn-primary:hover:not(:disabled) {
// //           background: var(--chili-dark);
// //         }

// //         .so-btn-primary:disabled {
// //           background: var(--stone-light);
// //           cursor: not-allowed;
// //           box-shadow: none;
// //         }

// //         .so-drawer {
// //           background: #fffdf8;
// //         }

// //         .so-drawer-close {
// //           color: var(--stone);
// //         }

// //         .so-drawer-close:hover {
// //           background: var(--paper-dim);
// //         }

// //         .so-invoice-overlay {
// //           background: rgba(28, 26, 23, 0.6);
// //         }

// //         .so-invoice-card {
// //           background: #fffdf8;
// //           border-radius: 12px;
// //         }

// //         .so-invoice-title {
// //           font-family: var(--font-display);
// //           font-size: 1.3rem;
// //           color: var(--ink);
// //         }

// //         .so-invoice-gstin {
// //           color: var(--ink);
// //         }

// //         .so-invoice-total {
// //           color: var(--herb-dark);
// //         }

// //         .so-invoice-btn-outline {
// //           border: 1px solid var(--line);
// //           color: var(--ink);
// //           background: #fff;
// //           transition: background 0.15s ease;
// //         }

// //         .so-invoice-btn-outline:hover:not(:disabled) {
// //           background: var(--paper-dim);
// //         }

// //         .so-invoice-btn-print {
// //           background: var(--herb);
// //           color: var(--paper);
// //         }

// //         .so-invoice-btn-print:hover:not(:disabled) {
// //           background: var(--herb-dark);
// //         }

// //         .so-invoice-btn-pdf {
// //           background: var(--ink);
// //           color: var(--paper);
// //         }

// //         .so-invoice-btn-pdf:hover:not(:disabled) {
// //           background: #302c27;
// //         }

// //         .so-invoice-btn-done {
// //           background: var(--chili);
// //           color: var(--paper);
// //           box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
// //         }

// //         .so-invoice-btn-done:hover:not(:disabled) {
// //           background: var(--chili-dark);
// //         }

// //         .so-invoice-btn-done:disabled {
// //           background: var(--stone-light);
// //         }
// //       `}</style>

// //       <div className="so-dpos flex flex-col md:flex-row h-full">

// //         <div className="flex-1 p-4 md:p-6 overflow-auto">

// //           <div className="flex items-center justify-between mb-5">
// //             <div>
// //               <div className="flex items-center gap-2">
// //                 <h2 className="so-page-title">
// //                   Direct POS
// //                 </h2>

// //                 <span className="so-badge-direct">
// //                   DIRECT
// //                 </span>
// //               </div>

// //               <p className="so-subtitle mt-1">
// //                 Fast billing without the kitchen queue
// //               </p>

// //               {gstEnabled && (
// //                 <p className="so-gst-note mt-1">
// //                   GST Enabled • GST added on top • Default GST {businessDefaultTaxRate}%
// //                 </p>
// //               )}
// //             </div>

// //             {isMobile && (
// //               <button
// //                 type="button"
// //                 onClick={() =>
// //                   setShowCartDrawer(true)
// //                 }
// //                 className="so-cart-toggle"
// //               >
// //                 <ShoppingCart size={18} />

// //                 <span>
// //                   Cart ({cart.length})
// //                 </span>
// //               </button>
// //             )}
// //           </div>

// //           <div className="so-panel p-4 mb-5">
// //             <div className="flex items-center justify-between mb-3">
// //               <div>
// //                 <h3 className="so-section-title">
// //                   Order Type
// //                 </h3>

// //                 <p className="so-section-sub">
// //                   Select how this customer is ordering
// //                 </p>
// //               </div>

// //               <span className="so-type-tag">
// //                 {formatOrderType(orderType)}
// //               </span>
// //             </div>

// //             <div className="grid grid-cols-2 gap-3">
// //               <button
// //                 type="button"
// //                 onClick={() =>
// //                   handleOrderTypeChange(
// //                     "TAKEAWAY"
// //                   )
// //                 }
// //                 className={`so-type-card ${
// //                   orderType === "TAKEAWAY"
// //                     ? "so-type-card-active-herb"
// //                     : ""
// //                 }`}
// //               >
// //                 <Package
// //                   size={25}
// //                   color={
// //                     orderType === "TAKEAWAY"
// //                       ? "#2d4f39"
// //                       : "#a49c8d"
// //                   }
// //                 />

// //                 <div className="font-bold so-text-ink mt-2">
// //                   Takeaway
// //                 </div>

// //                 <div className="so-section-sub mt-1">
// //                   Customer takes the order
// //                 </div>
// //               </button>

// //               <button
// //                 type="button"
// //                 onClick={() =>
// //                   handleOrderTypeChange(
// //                     "DINE_IN"
// //                   )
// //                 }
// //                 className={`so-type-card ${
// //                   orderType === "DINE_IN"
// //                     ? "so-type-card-active-chili"
// //                     : ""
// //                 }`}
// //               >
// //                 <Utensils
// //                   size={25}
// //                   color={
// //                     orderType === "DINE_IN"
// //                       ? "#c43f1a"
// //                       : "#a49c8d"
// //                   }
// //                 />

// //                 <div className="font-bold so-text-ink mt-2">
// //                   Dine In
// //                 </div>

// //                 <div className="so-section-sub mt-1">
// //                   Customer eats at restaurant
// //                 </div>
// //               </button>
// //             </div>
// //           </div>

// //           {orderType === "DINE_IN" && (
// //             <div className="so-panel p-4 mb-5">
// //               <div className="flex items-center justify-between mb-3">
// //                 <div>
// //                   <h3 className="so-section-title">
// //                     Select Table
// //                   </h3>

// //                   <p className="so-section-sub">
// //                     Choose an available table
// //                   </p>
// //                 </div>

// //                 {selectedTable && (
// //                   <button
// //                     type="button"
// //                     onClick={() =>
// //                       setSelectedTable(null)
// //                     }
// //                     className="so-clear-link"
// //                   >
// //                     Clear
// //                   </button>
// //                 )}
// //               </div>

// //               {loadingTables ? (
// //                 <div className="text-center py-6 so-text-stone">
// //                   Loading tables...
// //                 </div>
// //               ) : tables.length === 0 ? (
// //                 <div className="so-empty-note">
// //                   No restaurant tables found.
// //                 </div>
// //               ) : (
// //                 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
// //                   {tables
// //                     .filter(
// //                       (table) =>
// //                         table.active !== false
// //                     )
// //                     .map((table) => {
// //                       const selected =
// //                         selectedTable?.id ===
// //                         table.id;

// //                       const occupied =
// //                         table.status ===
// //                         "OCCUPIED";

// //                       const unavailable =
// //                         table.status ===
// //                         "OUT_OF_SERVICE";

// //                       return (
// //                         <button
// //                           key={table.id}
// //                           type="button"
// //                           disabled={
// //                             occupied ||
// //                             unavailable
// //                           }
// //                           onClick={() =>
// //                             handleTableSelect(
// //                               table
// //                             )
// //                           }
// //                           className={`so-table-card ${
// //                             selected
// //                               ? "so-table-card-selected"
// //                               : occupied
// //                               ? "so-table-card-occupied"
// //                               : unavailable
// //                               ? "so-table-card-unavailable"
// //                               : ""
// //                           }`}
// //                         >
// //                           <div className="font-bold so-text-ink">
// //                             {table.tableName ||
// //                               `Table ${table.id}`}
// //                           </div>

// //                           <div className="so-section-sub mt-1">
// //                             Capacity:{" "}
// //                             {table.capacity ||
// //                               "-"}
// //                           </div>

// //                           <div className="text-xs font-semibold mt-2 so-text-stone">
// //                             {selected
// //                               ? "Selected"
// //                               : occupied
// //                               ? "Occupied"
// //                               : unavailable
// //                               ? "Out of Service"
// //                               : "Available"}
// //                           </div>
// //                         </button>
// //                       );
// //                     })}
// //                 </div>
// //               )}

// //               {orderType === "DINE_IN" &&
// //                 !selectedTable && (
// //                   <p className="text-xs mt-3" style={{ color: "var(--chili-dark)" }}>
// //                     Please select a table.
// //                   </p>
// //                 )}
// //             </div>
// //           )}

// //           {orderType === "DINE_IN" &&
// //             selectedTable && (
// //               <div className="so-selected-table-banner mb-5">
// //                 <div className="flex items-center justify-between">
// //                   <div>
// //                     <p className="so-type-tag" style={{ color: "var(--chili-dark)" }}>
// //                       SELECTED TABLE
// //                     </p>

// //                     <p className="text-xl font-bold mt-1" style={{ color: "var(--chili-dark)" }}>
// //                       🍽️{" "}
// //                       {selectedTable.tableName}
// //                     </p>

// //                     <p className="text-xs mt-1" style={{ color: "var(--chili-dark)" }}>
// //                       Capacity:{" "}
// //                       {selectedTable.capacity}
// //                     </p>
// //                   </div>

// //                   <div className="so-dine-badge">
// //                     DINE IN
// //                   </div>
// //                 </div>
// //               </div>
// //             )}

// //           <div className="flex gap-2 mb-5 overflow-x-auto pb-2">
// //             <button
// //               type="button"
// //               onClick={() =>
// //                 setSelectedCategory("all")
// //               }
// //               className={`so-category-pill ${
// //                 selectedCategory === "all"
// //                   ? "so-category-pill-active"
// //                   : ""
// //               }`}
// //             >
// //               All Items
// //             </button>

// //             {categories.map((category) => (
// //               <button
// //                 type="button"
// //                 key={category.id}
// //                 onClick={() =>
// //                   setSelectedCategory(
// //                     category.id
// //                   )
// //                 }
// //                 className={`so-category-pill ${
// //                   String(
// //                     selectedCategory
// //                   ) === String(category.id)
// //                     ? "so-category-pill-active"
// //                     : ""
// //                 }`}
// //               >
// //                 {category.name}
// //               </button>
// //             ))}
// //           </div>

// //           {loadingMenu ? (
// //             <div className="text-center py-12 so-text-stone">
// //               Loading menu...
// //             </div>
// //           ) : filteredItems.length === 0 ? (
// //             <div className="text-center py-12 so-text-stone">
// //               No menu items available.
// //             </div>
// //           ) : (
// //             <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
// //               {filteredItems.map((item) => (
// //                 <button
// //                   type="button"
// //                   key={item.id}
// //                   onClick={() =>
// //                     addToCart(item)
// //                   }
// //                   className="so-item-card"
// //                 >
// //                   <div className="h-28 md:h-32 overflow-hidden flex items-center justify-center">
// //                     <img
// //                       src={`${API_BASE_URL}/api/menu-items/${item.id}/image`}
// //                       alt={item.name}
// //                       className="w-full h-full object-cover"
// //                       onError={(event) => {
// //                         if (item.imageUrl) {
// //                           event.currentTarget.src =
// //                             item.imageUrl;
// //                         } else {
// //                           event.currentTarget.style.display =
// //                             "none";

// //                           const parent =
// //                             event.currentTarget
// //                               .parentElement;

// //                           if (parent) {
// //                             parent.innerHTML =
// //                               PLATE_SPOON_SVG_MARKUP;
// //                           }
// //                         }
// //                       }}
// //                     />
// //                   </div>

// //                   <div className="p-3">
// //                     <h3 className="font-semibold so-text-ink text-sm md:text-base line-clamp-2">
// //                       {item.name}
// //                     </h3>

// //                     <div className="flex justify-between items-center mt-3">
// //                       <span className="so-item-price">
// //                         {formatCurrency(
// //                           item.price
// //                         )}
// //                       </span>

// //                       <span className="so-add-icon">
// //                         <Plus size={15} />
// //                       </span>
// //                     </div>

// //                     {gstEnabled && (
// //                       <div className="so-item-gst mt-1">
// //                         GST{" "}
// //                         {getItemTaxRate(item)}%
// //                         extra
// //                       </div>
// //                     )}
// //                   </div>
// //                 </button>
// //               ))}
// //             </div>
// //           )}
// //         </div>

// //         <aside className="so-cart-aside hidden md:flex md:w-96 p-5 flex-col">
// //           <div className="flex items-center justify-between mb-5">
// //             <div className="flex items-center gap-2">
// //               <ShoppingCart
// //                 size={24}
// //                 color="#1c1a17"
// //               />

// //               <h3 className="so-page-title" style={{ fontSize: "1.5rem" }}>
// //                 Direct Bill
// //               </h3>
// //             </div>

// //             <span className="so-badge-direct">
// //               DIRECT
// //             </span>
// //           </div>

// //           <div className="so-cart-summary mb-4">
// //             <div className="text-xs so-text-stone-light">
// //               Order Type
// //             </div>

// //             <div className="font-bold so-text-ink mt-1">
// //               {formatOrderType(orderType)}
// //             </div>

// //             {orderType === "DINE_IN" &&
// //               selectedTable && (
// //                 <div className="text-xs so-text-stone mt-1">
// //                   Table:{" "}
// //                   {selectedTable.tableName}
// //                 </div>
// //               )}
// //           </div>

// //           <div className="flex-1 overflow-auto">
// //             <CartContent />
// //           </div>
// //         </aside>
// //       </div>

// //       {isMobile &&
// //         showCartDrawer && (
// //           <div className="fixed inset-0 z-50">
// //             <div
// //               className="absolute inset-0 so-invoice-overlay"
// //               onClick={() =>
// //                 setShowCartDrawer(false)
// //               }
// //             />

// //             <div className="so-drawer absolute bottom-0 left-0 right-0 rounded-t-2xl max-h-[90vh] overflow-auto p-4">
// //               <div className="flex items-center justify-between mb-4">
// //                 <div className="flex items-center gap-2">
// //                   <ShoppingCart size={20} color="#1c1a17" />

// //                   <h3 className="text-xl font-bold so-text-ink">
// //                     Direct Bill
// //                   </h3>
// //                 </div>

// //                 <button
// //                   type="button"
// //                   onClick={() =>
// //                     setShowCartDrawer(false)
// //                   }
// //                   className="so-drawer-close p-2 rounded-lg"
// //                 >
// //                   <X size={20} />
// //                 </button>
// //               </div>

// //               <CartContent mobile />
// //             </div>
// //           </div>
// //         )}

// //       {showInvoice &&
// //         invoiceData && (
// //           <div className="fixed inset-0 z-[60] so-invoice-overlay flex items-center justify-center px-3">
// //             <div className="so-invoice-card shadow-2xl w-full max-w-md max-h-[95vh] overflow-auto p-4 md:p-6">

// //               <div
// //                 ref={receiptRef}
// //                 style={{
// //                   background: "white",
// //                   paddingBottom: 60,
// //                 }}
// //               >
// //                 <div className="text-center mb-4">
// //                   {logoUrl && (
// //                     <img
// //                       src={logoUrl}
// //                       alt="Business Logo"
// //                       className="h-14 mx-auto mb-2 object-contain"
// //                     />
// //                   )}

// //                   <h2 className="so-invoice-title">
// //                     {businessProfile?.businessName ||
// //                       "Restaurant POS"}
// //                   </h2>

// //                   {businessProfile?.addressLine1 && (
// //                     <p className="text-xs so-text-stone mt-1">
// //                       {
// //                         businessProfile.addressLine1
// //                       }
// //                     </p>
// //                   )}

// //                   {(businessProfile?.city ||
// //                     businessProfile?.state ||
// //                     businessProfile?.pinCode) && (
// //                     <p className="text-xs so-text-stone">
// //                       {[
// //                         businessProfile?.city,
// //                         businessProfile?.state,
// //                         businessProfile?.pinCode,
// //                       ]
// //                         .filter(Boolean)
// //                         .join(", ")}
// //                     </p>
// //                   )}

// //                   {businessProfile?.phone && (
// //                     <p className="text-xs so-text-stone">
// //                       Tel:{" "}
// //                       {businessProfile.phone}
// //                     </p>
// //                   )}

// //                   {businessProfile?.gstin && (
// //                     <p className="text-xs font-semibold so-invoice-gstin mt-1">
// //                       GSTIN:{" "}
// //                       {businessProfile.gstin}
// //                     </p>
// //                   )}
// //                 </div>

// //                 <div className="so-dashed-divider pt-3">
// //                   <div className="flex justify-between text-sm so-text-ink">
// //                     <span className="font-semibold">
// //                       Order
// //                     </span>

// //                     <span>
// //                       {invoiceData.orderNumber}
// //                     </span>
// //                   </div>

// //                   <div className="flex justify-between text-sm mt-1 so-text-ink">
// //                     <span className="font-semibold">
// //                       Type
// //                     </span>

// //                     <span>
// //                       {formatOrderType(
// //                         invoiceData.orderType
// //                       )}
// //                     </span>
// //                   </div>

// //                   {invoiceData.orderType ===
// //                     "DINE_IN" &&
// //                     invoiceData.table && (
// //                       <div className="flex justify-between text-sm mt-1 so-text-ink">
// //                         <span className="font-semibold">
// //                           Table
// //                         </span>

// //                         <span>
// //                           {
// //                             invoiceData.table
// //                               .tableName
// //                           }
// //                         </span>
// //                       </div>
// //                     )}

// //                   <div className="flex justify-between text-xs so-text-stone mt-1">
// //                     <span>Date</span>

// //                     <span>
// //                       {formatDateTime(
// //                         invoiceData.createdAt
// //                       )}
// //                     </span>
// //                   </div>
// //                 </div>

// //                 <div className="so-dashed-divider my-3 pt-3">
// //                   {invoiceData.items.map(
// //                     (item) => (
// //                       <div
// //                         key={item.id}
// //                         className="mb-3"
// //                       >
// //                         <div className="flex justify-between text-sm so-text-ink">
// //                           <div>
// //                             <span>
// //                               {item.name}
// //                             </span>

// //                             <span className="text-xs so-text-stone ml-2">
// //                               ×{" "}
// //                               {item.quantity}
// //                             </span>
// //                           </div>

// //                           <span className="font-medium">
// //                             {formatCurrency(
// //                               item.subtotal
// //                             )}
// //                           </span>
// //                         </div>

// //                         {gstEnabled &&
// //                           item.taxRate > 0 && (
// //                             <div className="flex justify-between text-xs so-text-stone mt-1">
// //                               <span>
// //                                 GST{" "}
// //                                 {item.taxRate}%
// //                               </span>

// //                               <span>
// //                                 +
// //                                 {formatCurrency(
// //                                   item.taxAmount
// //                                 )}
// //                               </span>
// //                             </div>
// //                           )}

// //                         <div className="flex justify-between text-xs font-semibold mt-1 so-text-ink">
// //                           <span>
// //                             Item Total
// //                           </span>

// //                           <span>
// //                             {formatCurrency(
// //                               item.lineTotal
// //                             )}
// //                           </span>
// //                         </div>
// //                       </div>
// //                     )
// //                   )}
// //                 </div>

// //                 <div className="so-dashed-divider pt-3">
// //                   <div className="flex justify-between text-sm so-text-ink">
// //                     <span>
// //                       Sub-total
// //                     </span>

// //                     <span>
// //                       {formatCurrency(
// //                         invoiceData.subtotal
// //                       )}
// //                     </span>
// //                   </div>

// //                   {invoiceData.gstEnabled &&
// //                     invoiceData.taxAmount > 0 && (
// //                       <div className="flex justify-between text-sm mt-2 so-text-ink">
// //                         <span>
// //                           GST
// //                         </span>

// //                         <span>
// //                           {formatCurrency(
// //                             invoiceData.taxAmount
// //                           )}
// //                         </span>
// //                       </div>
// //                     )}

// //                   <div className="flex justify-between text-lg font-bold mt-3 so-text-ink">
// //                     <span>TOTAL</span>

// //                     <span className="so-invoice-total">
// //                       {formatCurrency(
// //                         invoiceData.totalAmount
// //                       )}
// //                     </span>
// //                   </div>
// //                 </div>

// //                 {invoiceData.specialInstructions && (
// //                   <div className="so-dashed-divider mt-3 pt-3">
// //                     <p className="text-xs so-text-ink">
// //                       <span className="font-semibold">
// //                         Notes:
// //                       </span>{" "}
// //                       {
// //                         invoiceData.specialInstructions
// //                       }
// //                     </p>
// //                   </div>
// //                 )}

// //                 <p className="text-center text-sm so-text-stone mt-5">
// //                   Thank you for your purchase!
// //                 </p>
// //               </div>

// //               <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
// //                 <button
// //                   type="button"
// //                   onClick={() =>
// //                     setShowInvoice(false)
// //                   }
// //                   disabled={isProcessing}
// //                   className="so-invoice-btn-outline rounded-lg py-3 text-sm font-medium"
// //                 >
// //                   Back
// //                 </button>

// //                 <button
// //                   type="button"
// //                   onClick={handlePrint}
// //                   disabled={isProcessing}
// //                   className="so-invoice-btn-print rounded-lg py-3 text-sm font-semibold"
// //                 >
// //                   🖨 Print
// //                 </button>

// //                 <button
// //                   type="button"
// //                   onClick={handleDownloadPDF}
// //                   disabled={isProcessing}
// //                   className="so-invoice-btn-pdf rounded-lg py-3 text-sm font-semibold"
// //                 >
// //                   PDF
// //                 </button>
// //               </div>

// //               <button
// //                 type="button"
// //                 onClick={handleInvoiceDone}
// //                 disabled={isProcessing}
// //                 className="so-invoice-btn-done w-full mt-3 py-3 rounded-lg font-bold"
// //               >
// //                 {isProcessing
// //                   ? "Saving Order..."
// //                   : "✓ Done & Save Order"}
// //               </button>

// //               <p className="text-center text-xs so-text-stone mt-2">
// //                 This order will be saved directly
// //                 to Order History.
// //               </p>
// //             </div>
// //           </div>
// //         )}
// //     </>
// //   );
// // }









// // // src/components/DirectPOSView.js

// // import React, { useEffect, useRef, useState } from "react";
// // import {
// //   ShoppingCart,
// //   Plus,
// //   Minus,
// //   Trash2,
// //   Utensils,
// //   Package,
// //   X,
// // } from "lucide-react";

// // import {
// //   getAvailableMenuItems,
// //   getCategories,
// //   getTables,
// //   createDirectOrder,
// //   getBusinessProfile,
// //   getBusinessLogoBlob,
// // } from "../api";

// // import jsPDF from "jspdf";
// // import html2canvas from "html2canvas";

// // const API_BASE_URL =
// //   process.env.REACT_APP_API_URL || "http://localhost:8080";

// // export default function DirectPOSView() {
// //   const [menuItems, setMenuItems] = useState([]);
// //   const [categories, setCategories] = useState([]);
// //   const [selectedCategory, setSelectedCategory] = useState("all");

// //   const [cart, setCart] = useState([]);

// //   const [orderType, setOrderType] = useState("TAKEAWAY");
// //   const [tables, setTables] = useState([]);
// //   const [selectedTable, setSelectedTable] = useState(null);

// //   const [specialInstructions, setSpecialInstructions] = useState("");

// //   const [businessProfile, setBusinessProfile] = useState(null);
// //   const [logoUrl, setLogoUrl] = useState(null);

// //   const [showInvoice, setShowInvoice] = useState(false);
// //   const [invoiceData, setInvoiceData] = useState(null);

// //   const [isProcessing, setIsProcessing] = useState(false);
// //   const [loadingMenu, setLoadingMenu] = useState(true);
// //   const [loadingTables, setLoadingTables] = useState(true);

// //   const [showCartDrawer, setShowCartDrawer] = useState(false);
// //   const [isMobile, setIsMobile] = useState(
// //     typeof window !== "undefined" ? window.innerWidth < 768 : false
// //   );

// //   const receiptRef = useRef(null);

// //   useEffect(() => {
// //     const handleResize = () => {
// //       setIsMobile(window.innerWidth < 768);
// //     };

// //     window.addEventListener("resize", handleResize);

// //     return () => {
// //       window.removeEventListener("resize", handleResize);
// //     };
// //   }, []);

// //   useEffect(() => {
// //     loadMenu();
// //     loadCategories();
// //     loadTables();
// //     loadBusinessProfile();
// //   }, []);

// //   const loadMenu = async () => {
// //     try {
// //       setLoadingMenu(true);

// //       const data = await getAvailableMenuItems();

// //       setMenuItems(Array.isArray(data) ? data : []);
// //     } catch (error) {
// //       console.error("Failed to load menu:", error);
// //       alert("Unable to load menu items.");
// //     } finally {
// //       setLoadingMenu(false);
// //     }
// //   };

// //   const loadCategories = async () => {
// //     try {
// //       const data = await getCategories();

// //       setCategories(Array.isArray(data) ? data : []);
// //     } catch (error) {
// //       console.error("Failed to load categories:", error);
// //     }
// //   };

// //   const loadTables = async () => {
// //     try {
// //       setLoadingTables(true);

// //       const data = await getTables();

// //       setTables(Array.isArray(data) ? data : []);
// //     } catch (error) {
// //       console.error("Failed to load tables:", error);
// //       setTables([]);
// //     } finally {
// //       setLoadingTables(false);
// //     }
// //   };

// //   const loadBusinessProfile = async () => {
// //     try {
// //       const data = await getBusinessProfile();

// //       if (!data) return;

// //       setBusinessProfile(data);

// //       if (data.hasLogo) {
// //         try {
// //           const response = await getBusinessLogoBlob();

// //           const url = URL.createObjectURL(response.data);

// //           setLogoUrl(url);
// //         } catch (error) {
// //           console.error("Failed to load business logo:", error);
// //         }
// //       }
// //     } catch (error) {
// //       console.error("Failed to load business profile:", error);
// //     }
// //   };

// //   useEffect(() => {
// //     return () => {
// //       if (logoUrl) {
// //         URL.revokeObjectURL(logoUrl);
// //       }
// //     };
// //   }, [logoUrl]);

// //   const toNumber = (value) => {
// //     const number = Number(value);

// //     return Number.isFinite(number) ? number : 0;
// //   };

// //   const roundMoney = (value) => {
// //     return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100;
// //   };

// //   const gstEnabled = Boolean(businessProfile?.gstEnabled);

// //   const businessDefaultTaxRate = gstEnabled
// //     ? toNumber(businessProfile?.defaultTaxRate)
// //     : 0;

// //   const getItemTaxRate = (menuItem) => {
// //     const itemRate = toNumber(menuItem?.taxRate);

// //     if (itemRate > 0) {
// //       return itemRate;
// //     }

// //     return businessDefaultTaxRate;
// //   };

// //   /*
// //    * DIRECT POS GST IS ALWAYS EXTRA.
// //    *
// //    * ₹500 + 5% GST = ₹525
// //    * ₹500 + 3% GST = ₹515
// //    */
// //   const calculateLine = (menuItem, quantity) => {
// //     const price = roundMoney(menuItem?.price);

// //     const qty = Math.max(
// //       0,
// //       Math.floor(toNumber(quantity))
// //     );

// //     const subtotal = roundMoney(price * qty);

// //     if (!gstEnabled) {
// //       return {
// //         subtotal,
// //         taxRate: 0,
// //         taxAmount: 0,
// //         total: subtotal,
// //       };
// //     }

// //     const taxRate = roundMoney(
// //       getItemTaxRate(menuItem)
// //     );

// //     if (taxRate <= 0) {
// //       return {
// //         subtotal,
// //         taxRate: 0,
// //         taxAmount: 0,
// //         total: subtotal,
// //       };
// //     }

// //     const taxAmount = roundMoney(
// //       subtotal * taxRate / 100
// //     );

// //     const total = roundMoney(
// //       subtotal + taxAmount
// //     );

// //     return {
// //       subtotal,
// //       taxRate,
// //       taxAmount,
// //       total,
// //     };
// //   };

// //   const calculateCartTotals = () => {
// //     let subtotal = 0;
// //     let taxAmount = 0;
// //     let totalAmount = 0;

// //     cart.forEach((item) => {
// //       const line = calculateLine(
// //         item.menuItem,
// //         item.quantity
// //       );

// //       subtotal = roundMoney(
// //         subtotal + line.subtotal
// //       );

// //       taxAmount = roundMoney(
// //         taxAmount + line.taxAmount
// //       );

// //       totalAmount = roundMoney(
// //         totalAmount + line.total
// //       );
// //     });

// //     return {
// //       subtotal,
// //       taxAmount,
// //       totalAmount,
// //     };
// //   };

// //   const filteredItems =
// //     selectedCategory === "all"
// //       ? menuItems
// //       : menuItems.filter(
// //           (item) =>
// //             item.category &&
// //             String(item.category.id) ===
// //               String(selectedCategory)
// //         );

// //   const addToCart = (menuItem) => {
// //     setCart((previousCart) => {
// //       const existing = previousCart.find(
// //         (item) =>
// //           String(item.menuItem.id) ===
// //           String(menuItem.id)
// //       );

// //       if (existing) {
// //         return previousCart.map((item) =>
// //           String(item.menuItem.id) ===
// //           String(menuItem.id)
// //             ? {
// //                 ...item,
// //                 quantity: item.quantity + 1,
// //               }
// //             : item
// //         );
// //       }

// //       return [
// //         ...previousCart,
// //         {
// //           menuItem,
// //           quantity: 1,
// //         },
// //       ];
// //     });

// //     if (isMobile) {
// //       setShowCartDrawer(true);
// //     }
// //   };

// //   const updateQuantity = (menuItemId, change) => {
// //     setCart((previousCart) =>
// //       previousCart
// //         .map((item) =>
// //           String(item.menuItem.id) ===
// //           String(menuItemId)
// //             ? {
// //                 ...item,
// //                 quantity:
// //                   item.quantity + change,
// //               }
// //             : item
// //         )
// //         .filter((item) => item.quantity > 0)
// //     );
// //   };

// //   const removeFromCart = (menuItemId) => {
// //     setCart((previousCart) =>
// //       previousCart.filter(
// //         (item) =>
// //           String(item.menuItem.id) !==
// //           String(menuItemId)
// //       )
// //     );
// //   };

// //   const getTotalItems = () => {
// //     return cart.reduce(
// //       (total, item) =>
// //         total + item.quantity,
// //       0
// //     );
// //   };

// //   const handleOrderTypeChange = (type) => {
// //     setOrderType(type);

// //     if (type === "TAKEAWAY") {
// //       setSelectedTable(null);
// //     }
// //   };

// //   const handleTableSelect = (table) => {
// //     if (!table) return;

// //     if (table.status === "OCCUPIED") {
// //       alert(
// //         `${table.tableName} is currently occupied.`
// //       );
// //       return;
// //     }

// //     if (table.status === "OUT_OF_SERVICE") {
// //       alert(
// //         `${table.tableName} is out of service.`
// //       );
// //       return;
// //     }

// //     setSelectedTable(table);
// //   };

// //   const generateOrderNumber = () => {
// //     return `POS-${Date.now()}`;
// //   };

// //   const formatCurrency = (value) => {
// //     return new Intl.NumberFormat("en-IN", {
// //       style: "currency",
// //       currency: "INR",
// //       minimumFractionDigits: 2,
// //       maximumFractionDigits: 2,
// //     }).format(toNumber(value));
// //   };

// //   const formatDateTime = (value) => {
// //     if (!value) return "";

// //     return new Date(value).toLocaleString("en-IN");
// //   };

// //   const formatOrderType = (type) => {
// //     return type === "DINE_IN"
// //       ? "🍽️ DINE IN"
// //       : "🥡 TAKEAWAY";
// //   };

// //   const handleGenerateBill = () => {
// //     if (cart.length === 0) {
// //       alert("Please add at least one item.");
// //       return;
// //     }

// //     if (
// //       orderType === "DINE_IN" &&
// //       !selectedTable
// //     ) {
// //       alert("Please select a table for Dine In.");
// //       return;
// //     }

// //     const orderNumber = generateOrderNumber();

// //     const totals = calculateCartTotals();

// //     const invoiceItems = cart.map((item) => {
// //       const line = calculateLine(
// //         item.menuItem,
// //         item.quantity
// //       );

// //       return {
// //         id: item.menuItem.id,
// //         name: item.menuItem.name,
// //         price: roundMoney(item.menuItem.price),
// //         quantity: item.quantity,
// //         taxRate: line.taxRate,
// //         taxAmount: line.taxAmount,
// //         subtotal: line.subtotal,
// //         lineTotal: line.total,
// //       };
// //     });

// //     setInvoiceData({
// //       orderNumber,
// //       orderType,
// //       table: selectedTable,
// //       specialInstructions,
// //       createdAt: new Date().toISOString(),

// //       gstEnabled,

// //       taxInclusive: false,

// //       defaultTaxRate:
// //         businessDefaultTaxRate,

// //       subtotal: totals.subtotal,
// //       taxAmount: totals.taxAmount,
// //       totalAmount: totals.totalAmount,

// //       items: invoiceItems,
// //     });

// //     setShowInvoice(true);
// //   };

// //   const handleInvoiceDone = async () => {
// //     if (!invoiceData) return;

// //     setIsProcessing(true);

// //     try {
// //       const payload = {
// //         orderNumber:
// //           invoiceData.orderNumber,

// //         orderType:
// //           invoiceData.orderType,

// //         tableId:
// //           invoiceData.orderType === "DINE_IN"
// //             ? invoiceData.table?.id
// //             : null,

// //         specialInstructions:
// //           invoiceData.specialInstructions || null,

// //         items: invoiceData.items.map(
// //           (item) => ({
// //             menuItemId: item.id,
// //             quantity: item.quantity,

// //             /*
// //              * IMPORTANT:
// //              * Send ORIGINAL menu price.
// //              *
// //              * ₹500 is sent.
// //              * Backend calculates:
// //              * GST ₹25
// //              * Total ₹525
// //              */
// //             price: item.price,

// //             specialInstructions: null,

// //             taxRate: item.taxRate,
// //           })
// //         ),
// //       };

// //       const savedOrder =
// //         await createDirectOrder(payload);

// //       console.log(
// //         "DIRECT ORDER SAVED:",
// //         savedOrder
// //       );

// //       setCart([]);
// //       setOrderType("TAKEAWAY");
// //       setSelectedTable(null);
// //       setSpecialInstructions("");
// //       setInvoiceData(null);
// //       setShowInvoice(false);
// //       setShowCartDrawer(false);

// //       await loadTables();

// //       alert("Order saved successfully.");
// //     } catch (error) {
// //       console.error(
// //         "Direct POS order failed:",
// //         error
// //       );

// //       console.error(
// //         "Server response:",
// //         error?.response?.data
// //       );

// //       const serverMessage =
// //         error?.response?.data;

// //       alert(
// //         typeof serverMessage === "string"
// //           ? serverMessage
// //           : serverMessage?.message ||
// //             "Unable to save order."
// //       );
// //     } finally {
// //       setIsProcessing(false);
// //     }
// //   };

// //   const handlePrint = () => {
// //     if (!receiptRef.current) return;

// //     const content =
// //       receiptRef.current.innerHTML;

// //     const printWindow = window.open(
// //       "",
// //       "_blank",
// //       "width=420,height=750"
// //     );

// //     if (!printWindow) {
// //       alert(
// //         "Popup blocked. Please allow popups to print."
// //       );
// //       return;
// //     }

// //     printWindow.document.open();

// //     printWindow.document.write(`
// //       <html>
// //         <head>
// //           <title>Invoice</title>
// //           <meta
// //             name="viewport"
// //             content="width=device-width, initial-scale=1"
// //           />

// //           <style>
// //             * {
// //               box-sizing: border-box;
// //             }

// //             body {
// //               font-family: Arial, Helvetica, sans-serif;
// //               margin: 0;
// //               padding: 20px;
// //               background: white;
// //               color: #111827;
// //             }

// //             .row {
// //               display: flex;
// //               justify-content: space-between;
// //               gap: 10px;
// //             }

// //             img {
// //               max-width: 120px;
// //               max-height: 70px;
// //               object-fit: contain;
// //             }
// //           </style>
// //         </head>

// //         <body>
// //           ${content}
// //         </body>
// //       </html>
// //     `);

// //     printWindow.document.close();
// //     printWindow.focus();

// //     setTimeout(() => {
// //       printWindow.print();
// //       printWindow.close();
// //     }, 400);
// //   };

// //   const handleDownloadPDF = async () => {
// //     if (
// //       !receiptRef.current ||
// //       !invoiceData
// //     ) {
// //       return;
// //     }

// //     const element = receiptRef.current;

// //     const originalWidth =
// //       element.style.width;

// //     const originalMaxHeight =
// //       element.style.maxHeight;

// //     const originalPaddingBottom =
// //       element.style.paddingBottom;

// //     try {
// //       const targetWidth =
// //         window.innerWidth > 900
// //           ? 700
// //           : Math.floor(
// //               window.innerWidth * 0.92
// //             );

// //       element.style.width =
// //         `${targetWidth}px`;

// //       element.style.maxHeight = "none";
// //       element.style.paddingBottom = "60px";

// //       const canvas =
// //         await html2canvas(
// //           element,
// //           {
// //             scale:
// //               Math.min(
// //                 2,
// //                 window.devicePixelRatio ||
// //                   1.5
// //               ) * 1.2,

// //             useCORS: true,

// //             scrollX: 0,
// //             scrollY: -window.scrollY,

// //             windowWidth:
// //               element.scrollWidth,

// //             windowHeight:
// //               element.scrollHeight,
// //           }
// //         );

// //       const imageData =
// //         canvas.toDataURL("image/png");

// //       const pdf =
// //         new jsPDF(
// //           "p",
// //           "pt",
// //           "a4"
// //         );

// //       const pageWidth =
// //         pdf.internal.pageSize.getWidth();

// //       const pageHeight =
// //         pdf.internal.pageSize.getHeight();

// //       const margin = 20;

// //       const imageWidth =
// //         pageWidth - margin * 2;

// //       const imageHeight =
// //         (canvas.height *
// //           imageWidth) /
// //         canvas.width;

// //       let heightLeft =
// //         imageHeight;

// //       let position = margin;

// //       pdf.addImage(
// //         imageData,
// //         "PNG",
// //         margin,
// //         position,
// //         imageWidth,
// //         imageHeight
// //       );

// //       heightLeft -=
// //         pageHeight - margin * 2;

// //       while (heightLeft > 0) {
// //         pdf.addPage();

// //         position =
// //           margin -
// //           (
// //             imageHeight -
// //             heightLeft
// //           );

// //         pdf.addImage(
// //           imageData,
// //           "PNG",
// //           margin,
// //           position,
// //           imageWidth,
// //           imageHeight
// //         );

// //         heightLeft -=
// //           pageHeight - margin * 2;
// //       }

// //       pdf.save(
// //         `invoice_${invoiceData.orderNumber}.pdf`
// //       );
// //     } catch (error) {
// //       console.error(
// //         "PDF generation failed:",
// //         error
// //       );

// //       alert(
// //         "Failed to generate PDF."
// //       );
// //     } finally {
// //       element.style.width =
// //         originalWidth || "";

// //       element.style.maxHeight =
// //         originalMaxHeight || "";

// //       element.style.paddingBottom =
// //         originalPaddingBottom || "";
// //     }
// //   };

// //   const CartContent = ({ mobile = false } = {}) => {
// //     const totals =
// //       calculateCartTotals();

// //     return (
// //       <>
// //         {cart.length === 0 ? (
// //           <div className="text-center text-slate-400 py-10">
// //             <ShoppingCart
// //               size={48}
// //               className="mx-auto mb-3 opacity-40"
// //             />

// //             <p>Cart is empty</p>

// //             <p className="text-xs mt-1">
// //               Add menu items to continue.
// //             </p>
// //           </div>
// //         ) : (
// //           <div className="space-y-3">
// //             {cart.map((item) => {
// //               const line =
// //                 calculateLine(
// //                   item.menuItem,
// //                   item.quantity
// //                 );

// //               return (
// //                 <div
// //                   key={item.menuItem.id}
// //                   className="bg-slate-50 rounded-lg p-3"
// //                 >
// //                   <div className="flex justify-between items-start">
// //                     <div className="pr-2">
// //                       <h4 className="font-semibold text-slate-800">
// //                         {item.menuItem.name}
// //                       </h4>

// //                       <p className="text-xs text-slate-500 mt-1">
// //                         {formatCurrency(
// //                           item.menuItem.price
// //                         )}{" "}
// //                         each
// //                       </p>

// //                       {gstEnabled &&
// //                         line.taxRate > 0 && (
// //                           <p className="text-xs text-slate-500 mt-1">
// //                             GST{" "}
// //                             {line.taxRate}%
// //                             {" + "}
// //                             {formatCurrency(
// //                               line.taxAmount
// //                             )}
// //                           </p>
// //                         )}
// //                     </div>

// //                     <button
// //                       type="button"
// //                       onClick={() =>
// //                         removeFromCart(
// //                           item.menuItem.id
// //                         )
// //                       }
// //                       className="text-red-500 hover:text-red-700"
// //                     >
// //                       <Trash2 size={16} />
// //                     </button>
// //                   </div>

// //                   <div className="flex justify-between items-center mt-3">
// //                     <div className="flex items-center gap-2">
// //                       <button
// //                         type="button"
// //                         onClick={() =>
// //                           updateQuantity(
// //                             item.menuItem.id,
// //                             -1
// //                           )
// //                         }
// //                         className="w-8 h-8 bg-white rounded flex items-center justify-center border hover:bg-slate-100"
// //                       >
// //                         <Minus size={15} />
// //                       </button>

// //                       <span className="w-8 text-center font-semibold">
// //                         {item.quantity}
// //                       </span>

// //                       <button
// //                         type="button"
// //                         onClick={() =>
// //                           updateQuantity(
// //                             item.menuItem.id,
// //                             1
// //                           )
// //                         }
// //                         className="w-8 h-8 bg-white rounded flex items-center justify-center border hover:bg-slate-100"
// //                       >
// //                         <Plus size={15} />
// //                       </button>
// //                     </div>

// //                     <div className="text-right">
// //                       <div className="font-bold text-slate-800">
// //                         {formatCurrency(
// //                           line.total
// //                         )}
// //                       </div>

// //                       {gstEnabled &&
// //                         line.taxAmount > 0 && (
// //                           <div className="text-xs text-slate-500">
// //                             GST{" "}
// //                             {formatCurrency(
// //                               line.taxAmount
// //                             )}
// //                           </div>
// //                         )}
// //                     </div>
// //                   </div>
// //                 </div>
// //               );
// //             })}
// //           </div>
// //         )}

// //         <div className="mt-4">
// //           <label className="block text-sm font-semibold text-slate-700 mb-1">
// //             Order Notes
// //           </label>

// //           <textarea
// //             value={specialInstructions}
// //             onChange={(e) =>
// //               setSpecialInstructions(
// //                 e.target.value
// //               )
// //             }
// //             rows={3}
// //             maxLength={500}
// //             placeholder="e.g. Less spicy, extra sauce, no onion..."
// //             className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
// //           />

// //           <div className="text-right text-xs text-slate-400 mt-1">
// //             {specialInstructions.length}/500
// //           </div>
// //         </div>

// //         <div className="border-t mt-4 pt-4">
// //           <div className="flex justify-between text-sm mb-2">
// //             <span className="text-slate-500">
// //               {getTotalItems()} item
// //               {getTotalItems() !== 1
// //                 ? "s"
// //                 : ""}
// //             </span>
// //           </div>

// //           <div className="flex justify-between text-sm">
// //             <span>Sub-total</span>

// //             <span>
// //               {formatCurrency(
// //                 totals.subtotal
// //               )}
// //             </span>
// //           </div>

// //           {gstEnabled &&
// //             totals.taxAmount > 0 && (
// //               <div className="flex justify-between text-sm mt-2">
// //                 <span>
// //                   GST
// //                   {businessDefaultTaxRate > 0
// //                     ? ` (${businessDefaultTaxRate}%)`
// //                     : ""}
// //                 </span>

// //                 <span>
// //                   {formatCurrency(
// //                     totals.taxAmount
// //                   )}
// //                 </span>
// //               </div>
// //             )}

// //           <div className="flex justify-between items-center mt-3">
// //             <span className="text-xl font-bold text-slate-800">
// //               Total
// //             </span>

// //             <span className="text-2xl font-bold text-green-600">
// //               {formatCurrency(
// //                 totals.totalAmount
// //               )}
// //             </span>
// //           </div>

// //           <button
// //             type="button"
// //             onClick={() => {
// //               handleGenerateBill();

// //               if (mobile) {
// //                 setShowCartDrawer(false);
// //               }
// //             }}
// //             disabled={
// //               cart.length === 0 ||
// //               isProcessing
// //             }
// //             className="w-full mt-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-3 rounded-lg font-semibold transition"
// //           >
// //             🧾 Generate Bill
// //           </button>
// //         </div>
// //       </>
// //     );
// //   };

// //   return (
// //     <>
// //       <div className="flex flex-col md:flex-row h-full bg-slate-100">

// //         <div className="flex-1 p-4 md:p-6 overflow-auto">

// //           <div className="flex items-center justify-between mb-5">
// //             <div>
// //               <div className="flex items-center gap-2">
// //                 <h2 className="text-2xl md:text-3xl font-bold text-slate-800">
// //                   Direct POS
// //                 </h2>

// //                 <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-1 rounded-full">
// //                   DIRECT
// //                 </span>
// //               </div>

// //               <p className="text-sm text-slate-500 mt-1">
// //                 Fast billing without the kitchen queue
// //               </p>

// //               {gstEnabled && (
// //                 <p className="text-xs text-green-600 font-semibold mt-1">
// //                   GST Enabled • GST added on top • Default GST {businessDefaultTaxRate}%
// //                 </p>
// //               )}
// //             </div>

// //             {isMobile && (
// //               <button
// //                 type="button"
// //                 onClick={() =>
// //                   setShowCartDrawer(true)
// //                 }
// //                 className="flex items-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg"
// //               >
// //                 <ShoppingCart size={18} />

// //                 <span className="text-sm font-medium">
// //                   Cart ({cart.length})
// //                 </span>
// //               </button>
// //             )}
// //           </div>

// //           <div className="bg-white rounded-xl shadow-sm border p-4 mb-5">
// //             <div className="flex items-center justify-between mb-3">
// //               <div>
// //                 <h3 className="font-bold text-slate-800">
// //                   Order Type
// //                 </h3>

// //                 <p className="text-xs text-slate-500">
// //                   Select how this customer is ordering
// //                 </p>
// //               </div>

// //               <span className="text-xs font-semibold text-slate-500">
// //                 {formatOrderType(orderType)}
// //               </span>
// //             </div>

// //             <div className="grid grid-cols-2 gap-3">
// //               <button
// //                 type="button"
// //                 onClick={() =>
// //                   handleOrderTypeChange(
// //                     "TAKEAWAY"
// //                   )
// //                 }
// //                 className={`rounded-xl border-2 p-4 text-left transition ${
// //                   orderType === "TAKEAWAY"
// //                     ? "border-green-500 bg-green-50"
// //                     : "border-slate-200 bg-white hover:bg-slate-50"
// //                 }`}
// //               >
// //                 <Package
// //                   size={25}
// //                   className={
// //                     orderType === "TAKEAWAY"
// //                       ? "text-green-600"
// //                       : "text-slate-500"
// //                   }
// //                 />

// //                 <div className="font-bold text-slate-800 mt-2">
// //                   Takeaway
// //                 </div>

// //                 <div className="text-xs text-slate-500 mt-1">
// //                   Customer takes the order
// //                 </div>
// //               </button>

// //               <button
// //                 type="button"
// //                 onClick={() =>
// //                   handleOrderTypeChange(
// //                     "DINE_IN"
// //                   )
// //                 }
// //                 className={`rounded-xl border-2 p-4 text-left transition ${
// //                   orderType === "DINE_IN"
// //                     ? "border-blue-500 bg-blue-50"
// //                     : "border-slate-200 bg-white hover:bg-slate-50"
// //                 }`}
// //               >
// //                 <Utensils
// //                   size={25}
// //                   className={
// //                     orderType === "DINE_IN"
// //                       ? "text-blue-600"
// //                       : "text-slate-500"
// //                   }
// //                 />

// //                 <div className="font-bold text-slate-800 mt-2">
// //                   Dine In
// //                 </div>

// //                 <div className="text-xs text-slate-500 mt-1">
// //                   Customer eats at restaurant
// //                 </div>
// //               </button>
// //             </div>
// //           </div>

// //           {orderType === "DINE_IN" && (
// //             <div className="bg-white rounded-xl shadow-sm border p-4 mb-5">
// //               <div className="flex items-center justify-between mb-3">
// //                 <div>
// //                   <h3 className="font-bold text-slate-800">
// //                     Select Table
// //                   </h3>

// //                   <p className="text-xs text-slate-500">
// //                     Choose an available table
// //                   </p>
// //                 </div>

// //                 {selectedTable && (
// //                   <button
// //                     type="button"
// //                     onClick={() =>
// //                       setSelectedTable(null)
// //                     }
// //                     className="text-xs text-red-500 hover:text-red-700"
// //                   >
// //                     Clear
// //                   </button>
// //                 )}
// //               </div>

// //               {loadingTables ? (
// //                 <div className="text-center py-6 text-slate-500">
// //                   Loading tables...
// //                 </div>
// //               ) : tables.length === 0 ? (
// //                 <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
// //                   No restaurant tables found.
// //                 </div>
// //               ) : (
// //                 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
// //                   {tables
// //                     .filter(
// //                       (table) =>
// //                         table.active !== false
// //                     )
// //                     .map((table) => {
// //                       const selected =
// //                         selectedTable?.id ===
// //                         table.id;

// //                       const occupied =
// //                         table.status ===
// //                         "OCCUPIED";

// //                       const unavailable =
// //                         table.status ===
// //                         "OUT_OF_SERVICE";

// //                       return (
// //                         <button
// //                           key={table.id}
// //                           type="button"
// //                           disabled={
// //                             occupied ||
// //                             unavailable
// //                           }
// //                           onClick={() =>
// //                             handleTableSelect(
// //                               table
// //                             )
// //                           }
// //                           className={`p-3 rounded-xl border-2 text-center transition ${
// //                             selected
// //                               ? "border-blue-600 bg-blue-50"
// //                               : occupied
// //                               ? "border-red-200 bg-red-50 cursor-not-allowed"
// //                               : unavailable
// //                               ? "border-gray-200 bg-gray-100 cursor-not-allowed"
// //                               : "border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50"
// //                           }`}
// //                         >
// //                           <div className="font-bold text-slate-800">
// //                             {table.tableName ||
// //                               `Table ${table.id}`}
// //                           </div>

// //                           <div className="text-xs text-slate-500 mt-1">
// //                             Capacity:{" "}
// //                             {table.capacity ||
// //                               "-"}
// //                           </div>

// //                           <div className="text-xs font-semibold mt-2">
// //                             {selected
// //                               ? "Selected"
// //                               : occupied
// //                               ? "Occupied"
// //                               : unavailable
// //                               ? "Out of Service"
// //                               : "Available"}
// //                           </div>
// //                         </button>
// //                       );
// //                     })}
// //                 </div>
// //               )}

// //               {orderType === "DINE_IN" &&
// //                 !selectedTable && (
// //                   <p className="text-xs text-red-500 mt-3">
// //                     Please select a table.
// //                   </p>
// //                 )}
// //             </div>
// //           )}

// //           {orderType === "DINE_IN" &&
// //             selectedTable && (
// //               <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
// //                 <div className="flex items-center justify-between">
// //                   <div>
// //                     <p className="text-xs text-blue-600 font-semibold uppercase">
// //                       Selected Table
// //                     </p>

// //                     <p className="text-xl font-bold text-blue-800">
// //                       🍽️{" "}
// //                       {selectedTable.tableName}
// //                     </p>

// //                     <p className="text-xs text-blue-600 mt-1">
// //                       Capacity:{" "}
// //                       {selectedTable.capacity}
// //                     </p>
// //                   </div>

// //                   <div className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-semibold">
// //                     DINE IN
// //                   </div>
// //                 </div>
// //               </div>
// //             )}

// //           <div className="flex gap-2 mb-5 overflow-x-auto pb-2">
// //             <button
// //               type="button"
// //               onClick={() =>
// //                 setSelectedCategory("all")
// //               }
// //               className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium ${
// //                 selectedCategory === "all"
// //                   ? "bg-blue-600 text-white"
// //                   : "bg-white text-slate-700 hover:bg-slate-100"
// //               }`}
// //             >
// //               All Items
// //             </button>

// //             {categories.map((category) => (
// //               <button
// //                 type="button"
// //                 key={category.id}
// //                 onClick={() =>
// //                   setSelectedCategory(
// //                     category.id
// //                   )
// //                 }
// //                 className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium ${
// //                   String(
// //                     selectedCategory
// //                   ) === String(category.id)
// //                     ? "bg-blue-600 text-white"
// //                     : "bg-white text-slate-700 hover:bg-slate-100"
// //                 }`}
// //               >
// //                 {category.name}
// //               </button>
// //             ))}
// //           </div>

// //           {loadingMenu ? (
// //             <div className="text-center py-12 text-slate-500">
// //               Loading menu...
// //             </div>
// //           ) : filteredItems.length === 0 ? (
// //             <div className="text-center py-12 text-slate-500">
// //               No menu items available.
// //             </div>
// //           ) : (
// //             <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
// //               {filteredItems.map((item) => (
// //                 <button
// //                   type="button"
// //                   key={item.id}
// //                   onClick={() =>
// //                     addToCart(item)
// //                   }
// //                   className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-slate-200 transition overflow-hidden text-left"
// //                 >
// //                   <div className="h-28 md:h-32 bg-slate-200 overflow-hidden flex items-center justify-center">
// //                     <img
// //                       src={`${API_BASE_URL}/api/menu-items/${item.id}/image`}
// //                       alt={item.name}
// //                       className="w-full h-full object-cover"
// //                       onError={(event) => {
// //                         if (item.imageUrl) {
// //                           event.currentTarget.src =
// //                             item.imageUrl;
// //                         } else {
// //                           event.currentTarget.style.display =
// //                             "none";

// //                           const parent =
// //                             event.currentTarget
// //                               .parentElement;

// //                           if (parent) {
// //                             parent.innerHTML =
// //                               '<div class="w-full h-full flex items-center justify-center text-slate-400 text-sm">No Image</div>';
// //                           }
// //                         }
// //                       }}
// //                     />
// //                   </div>

// //                   <div className="p-3">
// //                     <h3 className="font-semibold text-slate-800 text-sm md:text-base line-clamp-2">
// //                       {item.name}
// //                     </h3>

// //                     <div className="flex justify-between items-center mt-3">
// //                       <span className="font-bold text-green-600">
// //                         {formatCurrency(
// //                           item.price
// //                         )}
// //                       </span>

// //                       <span className="bg-blue-100 text-blue-700 rounded-full p-1.5">
// //                         <Plus size={15} />
// //                       </span>
// //                     </div>

// //                     {gstEnabled && (
// //                       <div className="text-xs text-slate-500 mt-1">
// //                         GST{" "}
// //                         {getItemTaxRate(item)}%
// //                         extra
// //                       </div>
// //                     )}
// //                   </div>
// //                 </button>
// //               ))}
// //             </div>
// //           )}
// //         </div>

// //         <aside className="hidden md:flex md:w-96 bg-white shadow-xl p-5 flex-col">
// //           <div className="flex items-center justify-between mb-5">
// //             <div className="flex items-center gap-2">
// //               <ShoppingCart
// //                 size={24}
// //                 className="text-slate-700"
// //               />

// //               <h3 className="text-2xl font-bold text-slate-800">
// //                 Direct Bill
// //               </h3>
// //             </div>

// //             <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold">
// //               DIRECT
// //             </span>
// //           </div>

// //           <div className="bg-slate-50 rounded-lg p-3 mb-4">
// //             <div className="text-xs text-slate-500">
// //               Order Type
// //             </div>

// //             <div className="font-bold text-slate-800 mt-1">
// //               {formatOrderType(orderType)}
// //             </div>

// //             {orderType === "DINE_IN" &&
// //               selectedTable && (
// //                 <div className="text-xs text-slate-600 mt-1">
// //                   Table:{" "}
// //                   {selectedTable.tableName}
// //                 </div>
// //               )}
// //           </div>

// //           <div className="flex-1 overflow-auto">
// //             <CartContent />
// //           </div>
// //         </aside>
// //       </div>

// //       {isMobile &&
// //         showCartDrawer && (
// //           <div className="fixed inset-0 z-50">
// //             <div
// //               className="absolute inset-0 bg-black/50"
// //               onClick={() =>
// //                 setShowCartDrawer(false)
// //               }
// //             />

// //             <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[90vh] overflow-auto p-4">
// //               <div className="flex items-center justify-between mb-4">
// //                 <div className="flex items-center gap-2">
// //                   <ShoppingCart size={20} />

// //                   <h3 className="text-xl font-bold">
// //                     Direct Bill
// //                   </h3>
// //                 </div>

// //                 <button
// //                   type="button"
// //                   onClick={() =>
// //                     setShowCartDrawer(false)
// //                   }
// //                   className="p-2 rounded-lg hover:bg-slate-100"
// //                 >
// //                   <X size={20} />
// //                 </button>
// //               </div>

// //               <CartContent mobile />
// //             </div>
// //           </div>
// //         )}

// //       {showInvoice &&
// //         invoiceData && (
// //           <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center px-3">
// //             <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[95vh] overflow-auto p-4 md:p-6">

// //               <div
// //                 ref={receiptRef}
// //                 style={{
// //                   background: "white",
// //                   paddingBottom: 60,
// //                 }}
// //               >
// //                 <div className="text-center mb-4">
// //                   {logoUrl && (
// //                     <img
// //                       src={logoUrl}
// //                       alt="Business Logo"
// //                       className="h-14 mx-auto mb-2 object-contain"
// //                     />
// //                   )}

// //                   <h2 className="text-xl font-bold text-slate-800">
// //                     {businessProfile?.businessName ||
// //                       "Restaurant POS"}
// //                   </h2>

// //                   {businessProfile?.addressLine1 && (
// //                     <p className="text-xs text-slate-500 mt-1">
// //                       {
// //                         businessProfile.addressLine1
// //                       }
// //                     </p>
// //                   )}

// //                   {(businessProfile?.city ||
// //                     businessProfile?.state ||
// //                     businessProfile?.pinCode) && (
// //                     <p className="text-xs text-slate-500">
// //                       {[
// //                         businessProfile?.city,
// //                         businessProfile?.state,
// //                         businessProfile?.pinCode,
// //                       ]
// //                         .filter(Boolean)
// //                         .join(", ")}
// //                     </p>
// //                   )}

// //                   {businessProfile?.phone && (
// //                     <p className="text-xs text-slate-500">
// //                       Tel:{" "}
// //                       {businessProfile.phone}
// //                     </p>
// //                   )}

// //                   {businessProfile?.gstin && (
// //                     <p className="text-xs font-semibold text-slate-700 mt-1">
// //                       GSTIN:{" "}
// //                       {businessProfile.gstin}
// //                     </p>
// //                   )}
// //                 </div>

// //                 <div className="border-t border-dashed pt-3">
// //                   <div className="flex justify-between text-sm">
// //                     <span className="font-semibold">
// //                       Order
// //                     </span>

// //                     <span>
// //                       {invoiceData.orderNumber}
// //                     </span>
// //                   </div>

// //                   <div className="flex justify-between text-sm mt-1">
// //                     <span className="font-semibold">
// //                       Type
// //                     </span>

// //                     <span>
// //                       {formatOrderType(
// //                         invoiceData.orderType
// //                       )}
// //                     </span>
// //                   </div>

// //                   {invoiceData.orderType ===
// //                     "DINE_IN" &&
// //                     invoiceData.table && (
// //                       <div className="flex justify-between text-sm mt-1">
// //                         <span className="font-semibold">
// //                           Table
// //                         </span>

// //                         <span>
// //                           {
// //                             invoiceData.table
// //                               .tableName
// //                           }
// //                         </span>
// //                       </div>
// //                     )}

// //                   <div className="flex justify-between text-xs text-slate-500 mt-1">
// //                     <span>Date</span>

// //                     <span>
// //                       {formatDateTime(
// //                         invoiceData.createdAt
// //                       )}
// //                     </span>
// //                   </div>
// //                 </div>

// //                 <div className="border-t border-dashed my-3 pt-3">
// //                   {invoiceData.items.map(
// //                     (item) => (
// //                       <div
// //                         key={item.id}
// //                         className="mb-3"
// //                       >
// //                         <div className="flex justify-between text-sm">
// //                           <div>
// //                             <span>
// //                               {item.name}
// //                             </span>

// //                             <span className="text-xs text-slate-500 ml-2">
// //                               ×{" "}
// //                               {item.quantity}
// //                             </span>
// //                           </div>

// //                           <span className="font-medium">
// //                             {formatCurrency(
// //                               item.subtotal
// //                             )}
// //                           </span>
// //                         </div>

// //                         {gstEnabled &&
// //                           item.taxRate > 0 && (
// //                             <div className="flex justify-between text-xs text-slate-500 mt-1">
// //                               <span>
// //                                 GST{" "}
// //                                 {item.taxRate}%
// //                               </span>

// //                               <span>
// //                                 +
// //                                 {formatCurrency(
// //                                   item.taxAmount
// //                                 )}
// //                               </span>
// //                             </div>
// //                           )}

// //                         <div className="flex justify-between text-xs font-semibold mt-1">
// //                           <span>
// //                             Item Total
// //                           </span>

// //                           <span>
// //                             {formatCurrency(
// //                               item.lineTotal
// //                             )}
// //                           </span>
// //                         </div>
// //                       </div>
// //                     )
// //                   )}
// //                 </div>

// //                 <div className="border-t border-dashed pt-3">
// //                   <div className="flex justify-between text-sm">
// //                     <span>
// //                       Sub-total
// //                     </span>

// //                     <span>
// //                       {formatCurrency(
// //                         invoiceData.subtotal
// //                       )}
// //                     </span>
// //                   </div>

// //                   {invoiceData.gstEnabled &&
// //                     invoiceData.taxAmount > 0 && (
// //                       <div className="flex justify-between text-sm mt-2">
// //                         <span>
// //                           GST
// //                         </span>

// //                         <span>
// //                           {formatCurrency(
// //                             invoiceData.taxAmount
// //                           )}
// //                         </span>
// //                       </div>
// //                     )}

// //                   <div className="flex justify-between text-lg font-bold mt-3">
// //                     <span>TOTAL</span>

// //                     <span className="text-green-600">
// //                       {formatCurrency(
// //                         invoiceData.totalAmount
// //                       )}
// //                     </span>
// //                   </div>
// //                 </div>

// //                 {invoiceData.specialInstructions && (
// //                   <div className="border-t border-dashed mt-3 pt-3">
// //                     <p className="text-xs">
// //                       <span className="font-semibold">
// //                         Notes:
// //                       </span>{" "}
// //                       {
// //                         invoiceData.specialInstructions
// //                       }
// //                     </p>
// //                   </div>
// //                 )}

// //                 <p className="text-center text-sm text-slate-500 mt-5">
// //                   Thank you for your purchase!
// //                 </p>
// //               </div>

// //               <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
// //                 <button
// //                   type="button"
// //                   onClick={() =>
// //                     setShowInvoice(false)
// //                   }
// //                   disabled={isProcessing}
// //                   className="border border-slate-300 rounded-lg py-3 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
// //                 >
// //                   Back
// //                 </button>

// //                 <button
// //                   type="button"
// //                   onClick={handlePrint}
// //                   disabled={isProcessing}
// //                   className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-3 text-sm font-semibold disabled:opacity-50"
// //                 >
// //                   🖨 Print
// //                 </button>

// //                 <button
// //                   type="button"
// //                   onClick={handleDownloadPDF}
// //                   disabled={isProcessing}
// //                   className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-3 text-sm font-semibold disabled:opacity-50"
// //                 >
// //                   PDF
// //                 </button>
// //               </div>

// //               <button
// //                 type="button"
// //                 onClick={handleInvoiceDone}
// //                 disabled={isProcessing}
// //                 className="w-full mt-3 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white py-3 rounded-lg font-bold"
// //               >
// //                 {isProcessing
// //                   ? "Saving Order..."
// //                   : "✓ Done & Save Order"}
// //               </button>

// //               <p className="text-center text-xs text-slate-500 mt-2">
// //                 This order will be saved directly
// //                 to Order History.
// //               </p>
// //             </div>
// //           </div>
// //         )}
// //     </>
// //   );
// // } 
